/**
 * Importar módulos compartilhados
 */
// Assumindo que os módulos são carregados globalmente ou via script tags
// Se usando módulos ES6, descomente as linhas abaixo:
// import { AutocompleteManager } from '../shared/autocomplete.js';
// import { NfManager } from '../shared/nf-manager.js';
// import { MasksValidator } from '../shared/masks-validator.js';

/**
 * Módulo unificado para gerenciamento de formulários de ordens de serviço
 * Suporta tanto criação quanto edição de ordens de serviço
 */
class ServiceOrderFormManager {
    constructor(mode = "create", options = {}) {
        this.mode = mode; // 'create' ou 'edit'
        this.isEdit = mode === "edit";
        this.isCreate = mode === "create";

        // Estados do formulário
        this.submitConfirmed = false;
        this.isFormDirty = false;
        this.isSubmitting = false;
        this.notificationsLoaded = false;
        this.preservedValues = new Map(); // Armazenar valores preservados
        this.initializationComplete = false;

        // Handlers - apenas para métodos que existem
        this.handleFormSubmit = this.handleFormSubmit.bind(this);

        this.isEditing = false;
        this.serviceOrderId = null;

        // Detectar se é página de edição
        const editForm = document.querySelector(
            'form[action*="orders"][action*="update"], form[method="post"] input[name="_method"][value="PUT"]'
        );
        if (editForm) {
            this.isEditing = true;
            // Remover lógica de URL - o serviceOrderId pode ser obtido de outra forma se necessário
        }

        // Não precisamos mais preservar valores aqui, pois o initializeExistingSelects já faz isso

        // Handlers
        this.copyLinkHandler = null;
        this.whatsAppHandler = null;

        // Configurações padrão
        this.config = {
            routes: {
                searchProducts:
                    window.routes?.searchProducts ||
                    "/garage/orders/search-products",
                searchServices:
                    window.routes?.searchServices || "/OS/search-services",
            },
            csrfToken: $('meta[name="csrf-token"]').attr("content"),
            orderId: null,
            selectedClientId: null,
            selectedVehicleId: null,
            currentStatus: null,
            activePayment: false,
            ...options,
        };

        // Instâncias dos módulos compartilhados
        this.nfManager = null;
        this.autocompleteManager = null;
        this.masksValidator = null;

        this.init();
    }

    /**
     * Inicialização principal
     */
    init() {
        this.initModules();
        this.initCommonFeatures();

        if (this.isEdit) {
            this.initEditSpecificFeatures();
            // O método initializeExistingSelects já cuida da preservação e restauração de valores
        }

        if (this.isCreate) {
            this.initCreateSpecificFeatures();
        }
    }

    /**
     * Inicializar módulos compartilhados
     */
    initModules() {
        // Inicializar módulos compartilhados
        this.nfManager = window.NfManager
            ? new window.NfManager(this.config)
            : null;
        this.autocompleteManager =
            window.autocompleteManager ||
            (window.AutocompleteManager
                ? new window.AutocompleteManager()
                : null);
        this.masksValidator =
            window.masksValidator ||
            (window.MasksValidator ? new window.MasksValidator() : null);

        // Inicializar dados das NFs se fornecidos (para edit)
        if (this.config.nfsData && this.isEdit && this.nfManager) {
            this.nfManager.nfsData = this.config.nfsData;
        }

        // Inicializar autocomplete e máscaras
        if (this.autocompleteManager) {
            this.autocompleteManager.init();
        }
        if (this.masksValidator) {
            this.masksValidator.init();
        }
    }

    /**
     * Funcionalidades comuns entre create e edit
     */
    initCommonFeatures() {
        this.initDateHandling();
        this.initFormSubmission();
        this.setupAutocomplete();
        this.setupFormValidation();
        this.setupEventListeners();
        this.setupVehicleKmHandlers();
        this.initializeExistingSelects();
        this.restoreItemsFromStorage();
        this.setupQuickCreateButtons();
        this.initializeClientVehicleSelects();
        // Definir testes para máscaras no modal (apenas define, não executa)
        ServiceOrderFormManager.defineClientMaskTests();
    }

    /**
     * Funcionalidades específicas para edição
     */
    initEditSpecificFeatures() {
        this.setupStatusSelect();
        this.setupClientVehicleHandlers();
        this.setupDateHandlers();
        this.setupNotificationHandlers();
        this.setupEditAutocomplete();
        this.setupCopyLinkHandler();
        this.setupWhatsAppHandler();
        this.setupFormValidationAndModal();
        this.setupBasicFormValidation();

        // Ativar aba de pagamento se necessário
        if (this.config.activePayment) {
            let tabTrigger = new bootstrap.Tab(
                document.querySelector("#payment-tab")
            );
            tabTrigger.show();
        }

        // Recalcular totais após carregar os dados existentes
        setTimeout(() => {
            if (typeof this.calculateTotals === "function") {
                this.calculateTotals();
            }
        }, 100);

        // Carregar notificações após um delay
        setTimeout(() => {
            if (!this.notificationsLoaded) {
                this.loadNotifications();
            }
        }, 500);
    }

    /**
     * Funcionalidades específicas para criação
     */
    initCreateSpecificFeatures() {
        // Funcionalidades específicas para criação
        this.setupCreateFormModal();
    }

    setupQuickCreateButtons() {
        const self = this;
        $(document).on("click", ".button-quick-create-client", function () {
            self.openQuickCreateClientModal();
        });
        $(document).on("click", ".button-quick-create-vehicle", function () {
            self.openQuickCreateVehicleModal();
        });
    }

    openQuickCreateClientModal() {
        const self = this;
        $.get("/clientes/create", function (html) {
            const $dom = $("<div>").html(html);
            const $form = $dom.find('form[action*="/clientes/store"]').first();
            if ($form.length === 0) {
                alert("Não foi possível carregar o formulário de cliente");
                return;
            }
            $form.find('button[type="submit"], input[type="submit"]').remove();
            const currentGarageId =
                typeof window.garageId !== "undefined" && window.garageId
                    ? window.garageId
                    : $('input[name="oficinaVinculada"]').val() || "";
            if ($form.find('input[name="oficinaVinculada"]').length === 0) {
                $form.prepend(
                    `<input type="hidden" name="oficinaVinculada" value="${currentGarageId}">`
                );
            } else {
                $form
                    .find('input[name="oficinaVinculada"]')
                    .val(currentGarageId);
            }
            const modalEl = self.createBaseFormModal(
                "Cadastrar Cliente",
                $form.prop("outerHTML"),
                "quick-create-client-modal"
            );
            if (window.aplicarMascaras) {
                window.aplicarMascaras();
            }
            if (window.initMaskHandlers) {
                window.initMaskHandlers();
            }
            // CEP lookup for dynamically injected modal
            modalEl.on("input blur", ".inputCep", function () {
                const $el = $(this);
                const cep = ($el.val() || "").toString();
                const tipo = $el.data("form") === "PJ" ? "PJ" : "";
                if (typeof window.buscarEnderecoPorCep !== "function") return;
                window
                    .buscarEnderecoPorCep(cep, tipo)
                    .then(function (response) {
                        const data = response?.data?.result || {};
                        const uf = data.uf || "";
                        const cidade = data.localidade || "";
                        const bairro = data.bairro || "";
                        const logradouro = data.logradouro || "";
                        const $state = modalEl.find(`#inputState${tipo}`);
                        const $city = modalEl.find(`#inputCity${tipo}`);
                        const $district = modalEl.find(`#inputDistrict${tipo}`);
                        const $address = modalEl.find(`#inputAddress${tipo}`);
                        if ($state.length) $state.val(uf);
                        if ($address.length) $address.val(logradouro);
                        if ($district.length) $district.val(bairro);
                        if ($city.length) {
                            $.ajax({
                                url: `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`,
                                type: "GET",
                                dataType: "json",
                                success: function (municipios) {
                                    $city.empty();
                                    $city.append(new Option("Selecione", ""));
                                    municipios.forEach(function (m) {
                                        const opt = new Option(
                                            m.nome,
                                            m.nome,
                                            false,
                                            false
                                        );
                                        $city.append(opt);
                                    });
                                    if (cidade) {
                                        $city.val(cidade);
                                    }
                                },
                            });
                        }
                    })
                    .catch(function () {});
            });
            const onSubmit = () => {
                const $modalForm = modalEl.find("form");
                const formEl = $modalForm.get(0);
                $modalForm.addClass("was-validated");
                if (formEl && !formEl.checkValidity()) return;
                if (window.sanitizeMaskedFields) {
                    window.sanitizeMaskedFields($modalForm);
                }
                const btn = $("#modalBaseSubmit");
                btn.attr("disabled", true).html(
                    '<i class="sg sg-spinner-gap animate-spin"></i> Aguarde...'
                );
                $.ajax({
                    url: "/clientes/store",
                    type: "POST",
                    data: $modalForm.serialize(),
                    headers: {
                        "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr(
                            "content"
                        ),
                    },
                    success: function (response) {
                        // Atualiza o select da página principal
                        const $select = $("#selectClient");
                        if (
                            $select.find(
                                `option[value="${response.client.id}"]`
                            ).length === 0
                        ) {
                            const opt = new Option(
                                response.client.text,
                                response.client.id,
                                true,
                                true
                            );
                            $select.append(opt).trigger("change");
                        } else {
                            $select.val(response.client.id).trigger("change");
                        }

                        // Atualiza o select do modal de veículo, se existir
                        const $selectVehicleModal = $("#selectClientVehicleModal");
                        if ($selectVehicleModal.length > 0) {
                             if (
                                $selectVehicleModal.find(
                                    `option[value="${response.client.id}"]`
                                ).length === 0
                            ) {
                                const opt = new Option(
                                    response.client.text,
                                    response.client.id,
                                    true,
                                    true
                                );
                                $selectVehicleModal.append(opt).trigger("change");
                            } else {
                                $selectVehicleModal.val(response.client.id).trigger("change");
                            }
                        }

                        const modal = bootstrap.Modal.getInstance(modalEl[0]);
                        modal.hide();
                    },
                    error: function (xhr) {
                        const msg =
                            xhr.responseJSON && xhr.responseJSON.message
                                ? xhr.responseJSON.message
                                : "Erro ao cadastrar cliente";
                        modalEl
                            .find(".modal-body")
                            .prepend(
                                `<div class="alert alert-danger">${msg}</div>`
                            );
                    },
                    complete: function () {
                        btn.attr("disabled", false).html("Salvar");
                    },
                });
            };
            modalEl.find("#modalBaseSubmit").off("click").on("click", onSubmit);

            // Testes de interação das máscaras (executa quando habilitado)
            if (
                window.ClientModalMaskTests &&
                window.ClientModalMaskTests.enabled
            ) {
                try {
                    window.ClientModalMaskTests.run(modalEl);
                } catch (e) {}
            }
        });
    }

    // Testes de máscaras no modal de cliente
    static defineClientMaskTests() {
        if (window.ClientModalMaskTests) return;
        window.ClientModalMaskTests = {
            enabled: false,
            run: function (modalEl) {
                const $cpf = modalEl.find(".cpfMask");
                const $cel = modalEl.find(".telCelMask");
                const $cep = modalEl.find(".cepMask");
                const results = [];

                const typeSequence = function ($el, digits) {
                    $el.focus();
                    for (let i = 0; i < digits.length; i++) {
                        const prev = $el.val();
                        $el.val((prev || "") + digits[i]).trigger("input");
                    }
                };

                // CPF typing
                if ($cpf.length) {
                    $cpf.val("");
                    typeSequence($cpf, "12345678901");
                    results.push({
                        field: "cpf",
                        ok: ($cpf.val() || "").length === 14,
                    });
                }
                // Celular paste
                if ($cel.length) {
                    $cel.val("").trigger("input");
                    $cel.val("11987654321").trigger("input");
                    results.push({
                        field: "cel",
                        ok: ($cel.val() || "").length >= 14,
                    });
                }
                // CEP typing
                if ($cep.length) {
                    $cep.val("");
                    typeSequence($cep, "12345678");
                    results.push({
                        field: "cep",
                        ok: ($cep.val() || "").length === 9,
                    });
                }

                // Keyboard navigation simulation
                const $fields = modalEl.find("input, select");
                if ($fields.length >= 2) {
                    $fields.eq(0).focus();
                    const next = $fields.eq(1);
                    next.focus();
                    results.push({
                        field: "nav",
                        ok: document.activeElement === next.get(0),
                    });
                }

                // Mobile compatibility: inputmode
                const mobileOk =
                    $cpf.attr("inputmode") === "numeric" &&
                    $cel.attr("inputmode") === "tel";
                results.push({ field: "mobile", ok: !!mobileOk });

                console.log("ClientModalMaskTests:", results);
            },
        };
    }

    openQuickCreateVehicleModal() {
        const self = this;
        const mainClientId = $("#selectClient").val();
        const mainClientData = $("#selectClient").select2('data')[0];

        $.get("/veiculos/create", function (html) {
            const $dom = $("<div>").html(html);
            const $form = $dom.find("form#createVehicle").first();
            if ($form.length === 0) {
                alert("Não foi possível carregar o formulário de veículo");
                return;
            }
            
            // Adjust ID to avoid conflict with main form
            const $modalSelectClient = $form.find("#selectClient");
            $modalSelectClient.attr("id", "selectClientVehicleModal");
            
            $form.find('button[type="submit"], input[type="submit"]').remove();
            
            if ($form.find('input[name="oficinaVinculada"]').length === 0) {
                $form.prepend(
                    `<input type="hidden" name="oficinaVinculada" value="${
                        window.garageId || ""
                    }">`
                );
            } else {
                 // Only update if window.garageId is explicitly defined and not empty
                 // This prevents overwriting the server-provided value with empty strings
                 if (typeof window.garageId !== "undefined" && window.garageId && String(window.garageId).trim() !== "") {
                    $form
                        .find('input[name="oficinaVinculada"]')
                        .val(window.garageId);
                 }
            }
            const modalEl = self.createBaseFormModal(
                "Cadastrar Veículo",
                $form.prop("outerHTML"),
                "quick-create-vehicle-modal"
            );

            // Re-select the element from the modal (since it was cloned)
            const $selectClient = modalEl.find("#selectClientVehicleModal");
            
            // Add bootstrap class
            $selectClient.addClass('form-select');

            // Initialize Select2 for the client select in modal
            $selectClient.select2({
                language: "pt-BR",
                dropdownParent: modalEl,
                placeholder: "Selecione um cliente",
                allowClear: true,
                width: "100%",
                ajax: {
                    url: "/clientes/getClients",
                    type: "POST",
                    delay: 300,
                    data: (params) => ({ value: params.term || "" }),
                    headers: {
                        "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr("content"),
                    },
                    processResults: function (response) {
                        const results = [];
                        results.push({
                            id: "__add_client__",
                            text: "Adicionar cliente",
                            isAddButton: true,
                        });
                        const items = response.clients || response.results;
                        if (items) {
                            items.forEach((item) => {
                                const text = item.text || (item.tipo_cliente === "PF"
                                        ? item.nome || ""
                                        : item.razao_social || "");
                                results.push({ id: item.id, text: text });
                            });
                        }
                        return { results };
                    },
                },
                language: {
                    noResults: () => {
                        return '<span class="select2-add-client" style="cursor:pointer; color: #007bff; font-weight: bold;"><i class="sg sg-plus-circle me-1"></i>Adicionar cliente</span>';
                    },
                },
                escapeMarkup: function (markup) {
                    return markup;
                },
                templateResult: function (data) {
                    if (data.isAddButton) {
                        return $(
                            '<span style="color: #007bff; font-weight: bold;"><i class="sg sg-plus-circle me-1"></i>Adicionar cliente</span>'
                        );
                    }
                    return data.text;
                }
            })
            .on("select2:selecting", function (e) {
                const data = e.params.args.data;
                if (data.isAddButton) {
                    e.preventDefault();
                    self.openQuickCreateClientModal();
                    $(this).select2("close");
                }
            })
            .on("select2:open", function () {
                const $dropdownMsg = $(".select2-results__message");
                $dropdownMsg.off("click.select2Add");
                $dropdownMsg.on(
                    "click.select2Add",
                    ".select2-add-client",
                    function (ev) {
                        ev.preventDefault();
                        self.openQuickCreateClientModal();
                        $selectClient.select2("close");
                    }
                );
            });

            // Function to update client card
            const updateClientCard = (clientId) => {
                 if (!clientId) {
                     modalEl.find("#clientSelected").empty();
                     modalEl.find(".card-result-client").hide();
                     return;
                 }
                 
                 $.ajax({
                    url: `/clientes/getClientById/${clientId}`,
                    type: "POST",
                    headers: {
                        "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr("content"),
                    },
                    success: function (response) {
                        if (response.success && response.results) {
                            const clientData = response.results;
                            let telefone = "Não informado";
                            if (clientData.telefone_celular)
                                telefone = self.formatPhone(clientData.telefone_celular);
                            else if (clientData.telefone_fixo)
                                telefone = self.formatPhone(clientData.telefone_fixo);
                                
                            const nomeExibicao = clientData.tipo_cliente === "PF"
                                    ? clientData.nome || "Nome não informado"
                                    : clientData.razao_social || "Razão social não informada";
                                    
                            const cardHtml = `
                                <div>
                                    <span class="icon-box"><i class="sg sg-user"></i></span>
                                    <div>
                                        <span class="d-block">${nomeExibicao}</span>
                                    </div>
                                </div>
                                <hr>
                                <div>
                                    <span class="icon-box"><i class="sg sg-phone"></i></span>
                                    <span>${telefone}</span>
                                </div>`;
                            
                            const $container = modalEl.find(".card-result-client");
                            const $clientSelected = modalEl.find("#clientSelected");
                            $clientSelected.html(cardHtml);
                            $container.show();
                        }
                    },
                });
            };

            // Bind change event
            $selectClient.on('change', function() {
                updateClientCard($(this).val());
            });

            // Pre-select client if available
            if (mainClientId) {
                // If we have the data from the main select2, use it to avoid fetching
                if (mainClientData && mainClientData.text) {
                     const option = new Option(mainClientData.text, mainClientId, true, true);
                     $selectClient.append(option).trigger('change');
                } else {
                     // Fallback if data is missing (should not happen usually)
                     $selectClient.val(mainClientId).trigger('change');
                }
            }

            if (window.aplicarMascaras) {
                window.aplicarMascaras();
            }
            if (
                window.masksValidator &&
                typeof window.masksValidator.setupPlateMasks === "function"
            ) {
                try {
                    window.masksValidator.setupPlateMasks();
                } catch (e) {}
            }
            const $plateInputEnsure = modalEl.find("#inputPlate");
            $plateInputEnsure
                .removeAttr("readonly")
                .removeAttr("disabled")
                .prop("disabled", false)
                .prop("readonly", false)
                .attr("tabindex", 0)
                .removeAttr("aria-disabled")
                .css({
                    "pointer-events": "auto",
                    "user-select": "auto",
                    opacity: "1",
                });
            $plateInputEnsure.closest(".disabled").removeClass("disabled");
            if (typeof $plateInputEnsure.unmask === "function") {
                try {
                    $plateInputEnsure.unmask();
                } catch (e) {}
            }
            $plateInputEnsure
                .removeAttr("maxlength")
                .attr("autocomplete", "off");
            if (
                typeof $.fn.mask !== "undefined" &&
                typeof $plateInputEnsure.mask === "function"
            ) {
                try {
                    $plateInputEnsure.mask("AAA-0S00", {
                        placeholder: "ABC-1D23",
                        translation: {
                            A: { pattern: /[A-Za-z]/ },
                            0: { pattern: /[0-9]/ },
                            S: { pattern: /[A-Za-z0-9]/ },
                        },
                    });
                } catch (e) {}
            }
            $plateInputEnsure.on("keyup", function () {
                const v = ($(this).val() || "").toUpperCase();
                const cleaned = v.replace(/[^A-Z0-9\-]/g, "");
                if ($(this).val() !== cleaned) $(this).val(cleaned);
            });
            modalEl.on("shown.bs.modal", function () {
                setTimeout(() => {
                    $plateInputEnsure.focus();
                }, 50);
                if (
                    typeof $.fn.mask !== "undefined" &&
                    typeof $plateInputEnsure.mask === "function"
                ) {
                    try {
                        $plateInputEnsure.unmask();
                        $plateInputEnsure.mask("AAA-0S00", {
                            placeholder: "ABC-1D23",
                            translation: {
                                A: { pattern: /[A-Za-z]/ },
                                0: { pattern: /[0-9]/ },
                                S: { pattern: /[A-Za-z0-9]/ },
                            },
                        });
                    } catch (e) {}
                }
            });
            const tipoInicial = modalEl.find("#tipoVeiculo").val() || "carros";
            const loadBrands = (tipo) => {
                $.ajax({
                    url: `/veiculos/obter-marcas/${tipo}`,
                    type: "GET",
                    success: function (brands) {
                        const $brand = modalEl.find("#inputBrand");
                        $brand
                            .empty()
                            .append(
                                '<option value="" selected>Selecione a marca</option>'
                            );
                        brands.forEach(function (b) {
                            const val = b.codMarca || b.id || "";
                            const txt = b.nomeMarca || b.nome || "";
                            if (val)
                                $brand.append(
                                    `<option value="${val}">${txt}</option>`
                                );
                        });
                    },
                });
            };
            const loadModels = (codMarca, tipo) => {
                if (!codMarca) return;
                $.ajax({
                    url: `/veiculos/obter-modelos/${codMarca}?tipo=${tipo}`,
                    type: "GET",
                    success: function (models) {
                        const $model = modalEl.find("#inputModel");
                        $model
                            .empty()
                            .append(
                                '<option value="" selected>Selecione o modelo</option>'
                            );
                        models.forEach(function (m) {
                            const val = m.codModelo || m.id || "";
                            const txt = m.nomeModelo || m.nome || "";
                            if (val)
                                $model.append(
                                    `<option value="${val}">${txt}</option>`
                                );
                        });
                    },
                });
            };
            loadBrands(tipoInicial);
            modalEl.on("change", "#inputBrand", function () {
                const codMarca = $(this).val();
                const tipo = modalEl.find("#tipoVeiculo").val() || "carros";
                loadModels(codMarca, tipo);
            });
            modalEl.on("click", ".vehicle-type-button", function () {
                const t = $(this).data("tipo");
                modalEl.find("#tipoVeiculo").val(t);
                loadBrands(t);
                modalEl
                    .find("#inputModel")
                    .empty()
                    .append(
                        '<option value="" selected>Selecione o modelo</option>'
                    );
            });
            const onSubmit = () => {
                const $modalForm = modalEl.find("form");
                const formEl = $modalForm.get(0);
                $modalForm.addClass("was-validated");
                if (formEl && !formEl.checkValidity()) return;
                const plateVal = ($modalForm.find("#inputPlate").val() || "")
                    .replace(/[^A-Z0-9]/g, "")
                    .toUpperCase();
                if (plateVal.length < 7) {
                    const cont = modalEl.find(".modal-body");
                    cont.find(".alert").remove();
                    cont.prepend(
                        '<div class="alert alert-warning">Informe uma placa válida.</div>'
                    );
                    return;
                }
                const btn = $("#modalBaseSubmit");
                btn.attr("disabled", true).html(
                    '<i class="sg sg-spinner-gap animate-spin"></i> Aguarde...'
                );
                $.ajax({
                    url: "/veiculos/store",
                    type: "POST",
                    data: $modalForm.serialize(),
                    headers: {
                        "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr(
                            "content"
                        ),
                    },
                    success: function (response) {
                        if (response.success === false) {
                            const msg = response.message || "Erro ao cadastrar veículo";
                            
                            // Remove alertas anteriores para não duplicar (busca global no modal)
                            modalEl.find(".alert").remove();
                            
                            modalEl
                                .find(".modal-body")
                                .prepend(
                                    `<div class="alert alert-danger alert-dismissible fade show" role="alert">
                                        ${msg}
                                        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"><i class="sg sg-x"></i></button>
                                    </div>`
                                );
                            return;
                        }

                        const $select = $("#selectVehicle");
                        const triggerSelect = () => {
                             $select.trigger({
                                type: 'select2:select',
                                params: {
                                    data: response.vehicle
                                }
                            });
                        };

                        if (
                            $select.find(
                                `option[value="${response.vehicle.id}"]`
                            ).length === 0
                        ) {
                            const opt = new Option(
                                response.vehicle.text,
                                response.vehicle.id,
                                true,
                                true
                            );
                            $select.append(opt).trigger("change");
                            triggerSelect();
                        } else {
                            $select.val(response.vehicle.id).trigger("change");
                            triggerSelect();
                        }
                        const modal = bootstrap.Modal.getInstance(modalEl[0]);
                        modal.hide();
                    },
                    error: function (xhr) {
                        const msg =
                            xhr.responseJSON && xhr.responseJSON.message
                                ? xhr.responseJSON.message
                                : "Erro ao cadastrar veículo";
                        
                        // Remove alertas anteriores para não duplicar
                        modalEl.find(".modal-body .alert").remove();
                        
                        modalEl
                            .find(".modal-body")
                            .prepend(
                                `<div class="alert alert-danger alert-dismissible fade show" role="alert">
                                    ${msg}
                                    <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"><i class="sg sg-x"></i></button>
                                </div>`
                            );
                    },
                    complete: function () {
                        btn.attr("disabled", false).html("Salvar");
                    },
                });
            };
            modalEl.find("#modalBaseSubmit").off("click").on("click", onSubmit);



            // Busca de placa no modal (com debounce) reaproveitando endpoint existente
            const $plateInput = modalEl.find("#inputPlate");
            // Remover handlers que possam bloquear a digitação - REMOVIDO pois remove a máscara também
            // $plateInput.off('keydown keypress keyup input blur change');
            let searchTimeout;
            $plateInput.on("input", function () {
                const rawValue = $(this).val();
                const plate = rawValue.replace(/[^A-Z0-9]/g, "").toUpperCase();
                if (searchTimeout) clearTimeout(searchTimeout);
                if (plate.length >= 7) {
                    searchTimeout = setTimeout(() => {
                        $.ajax({
                            url: "/veiculos/buscar-dados",
                            method: "POST",
                            data: {
                                placa: plate,
                                _token: $('meta[name="csrf-token"]').attr(
                                    "content"
                                ),
                            },
                            success: function (response) {
                                if (response.success && response.data) {
                                    const data =
                                        typeof response.data === "string"
                                            ? JSON.parse(response.data)
                                            : response.data;
                                    const $brand = modalEl.find("#inputBrand");
                                    const $model = modalEl.find("#inputModel");
                                    const normalizeText = function (t) {
                                        return (t || "")
                                            .toString()
                                            .normalize("NFD")
                                            .replace(/[\u0300-\u036f]/g, "")
                                            .toUpperCase()
                                            .trim();
                                    };
                                    const brandMap = {
                                        LR: "Land Rover",
                                        "M.BENZ": "Mercedes-Benz",
                                        KIA: "Kia Motors",
                                        TOYOTA: "Toyota",
                                        T: "Hyundai",
                                        MMC: "Mitsubishi",
                                        VW: "VolksWagen",
                                        CHEV: "Chevrolet",
                                        GM: "Chevrolet",
                                        MB: "Mercedes-Benz",
                                    };
                                    const fipeDados = Array.isArray(
                                        data?.fipe?.dados
                                    )
                                        ? data.fipe.dados
                                        : [];
                                    const brandCandidates = [];
                                    if (
                                        fipeDados.length &&
                                        fipeDados[0]?.texto_marca
                                    ) {
                                        const bm =
                                            fipeDados[0].texto_marca.toString();
                                        const parts = bm.split(" - ");
                                        brandCandidates.push(
                                            parts.length > 1
                                                ? parts[parts.length - 1]
                                                : bm
                                        );
                                    }
                                    if (data.MARCA)
                                        brandCandidates.push(data.MARCA);
                                    if (data.marca)
                                        brandCandidates.push(data.marca);
                                    if (data?.extra?.modelo) {
                                        const mm = data.extra.modelo.toString();
                                        const segs = mm.split("/");
                                        if (segs.length > 1)
                                            brandCandidates.push(
                                                segs[1].split(" ")[0]
                                            );
                                    }
                                    const resolvedBrand = (function () {
                                        for (
                                            let i = 0;
                                            i < brandCandidates.length;
                                            i++
                                        ) {
                                            let b = brandCandidates[i] || "";
                                            b = brandMap[b] || b;
                                            const bNorm = normalizeText(b);
                                            const optExact = $brand
                                                .find("option")
                                                .filter(function () {
                                                    return (
                                                        normalizeText(
                                                            $(this).text()
                                                        ) === bNorm
                                                    );
                                                });
                                            if (optExact.length)
                                                return {
                                                    val: optExact.val(),
                                                    text: optExact.text(),
                                                };
                                            const optIncl = $brand
                                                .find("option")
                                                .filter(function () {
                                                    return normalizeText(
                                                        $(this).text()
                                                    ).includes(bNorm);
                                                })
                                                .first();
                                            if (optIncl.length)
                                                return {
                                                    val: optIncl.val(),
                                                    text: optIncl.text(),
                                                };
                                        }
                                        return null;
                                    })();
                                    if (resolvedBrand) {
                                        $brand
                                            .val(resolvedBrand.val)
                                            .trigger("change");
                                    }
                                    setTimeout(function () {
                                        const modelCandidates = [];
                                        if (
                                            fipeDados.length &&
                                            fipeDados[0]?.texto_modelo
                                        )
                                            modelCandidates.push(
                                                fipeDados[0].texto_modelo
                                            );
                                        if (
                                            data.MODELO &&
                                            data.MODELO !== "null"
                                        )
                                            modelCandidates.push(data.MODELO);
                                        if (
                                            data.modelo &&
                                            data.modelo !== "null"
                                        )
                                            modelCandidates.push(data.modelo);
                                        if (data.marcaModelo) {
                                            const p =
                                                data.marcaModelo.split("/");
                                            if (p.length > 1)
                                                modelCandidates.push(p[1]);
                                        }
                                        if (data.marcamodelo) {
                                            const p2 =
                                                data.marcamodelo.split("/");
                                            if (p2.length > 1)
                                                modelCandidates.push(p2[1]);
                                        }
                                        if (Array.isArray(data.listamodelo))
                                            modelCandidates.push(
                                                data.listamodelo.join(" ")
                                            );
                                        if (data?.extra?.modelo)
                                            modelCandidates.push(
                                                data.extra.modelo
                                            );
                                        const originalHtml =
                                            $model.data("original-options") ||
                                            $model.html();
                                        $model.data(
                                            "original-options",
                                            originalHtml
                                        );
                                        const placeholder = $model
                                            .find("option")
                                            .filter(function () {
                                                return (
                                                    ($(this).val() || "") === ""
                                                );
                                            })
                                            .first();
                                        const opts = $model.find("option");
                                        const tokenSynonyms = function (tok) {
                                            const t = tok.toUpperCase();
                                            if (
                                                t === "ALLSPAC" ||
                                                t === "ALLSPACE"
                                            )
                                                return ["ALLSPACE", "ALLSPAC"];
                                            return [t];
                                        };
                                        const stop = new Set([
                                            "TSI",
                                            "16V",
                                            "CV",
                                            "G",
                                            "F",
                                            "FLEX",
                                            "MEC",
                                            "AUT",
                                            "AT",
                                            "MT",
                                        ]);
                                        const resolvedBrandText = resolvedBrand
                                            ? normalizeText(resolvedBrand.text)
                                            : "";
                                        const brandTokens = resolvedBrandText
                                            ? resolvedBrandText
                                                  .split(/\W+/)
                                                  .filter((x) => x.length > 2)
                                            : [];
                                        const tokenFreq = {};
                                        modelCandidates.forEach(function (c) {
                                            const m = normalizeText(c);
                                            if (!m) return;
                                            m.split(/\W+/).forEach(function (
                                                tok
                                            ) {
                                                const T = tok.toUpperCase();
                                                if (
                                                    T.length > 2 &&
                                                    !stop.has(T) &&
                                                    !brandTokens.includes(T)
                                                ) {
                                                    const canon =
                                                        tokenSynonyms(T)[0];
                                                    tokenFreq[canon] =
                                                        (tokenFreq[canon] ||
                                                            0) + 1;
                                                }
                                            });
                                        });
                                        const sortedTokens = Object.keys(
                                            tokenFreq
                                        ).sort(function (a, b) {
                                            return tokenFreq[b] - tokenFreq[a];
                                        });
                                        let primary =
                                            sortedTokens.find(
                                                (t) => t.length >= 4
                                            ) ||
                                            sortedTokens[0] ||
                                            "";
                                        let secondary = "";
                                        if (
                                            sortedTokens.includes("ALLSPACE") ||
                                            sortedTokens.includes("ALLSPAC")
                                        )
                                            secondary = "ALLSPACE";
                                        let required = primary ? [primary] : [];
                                        if (secondary) required.push(secondary);
                                        const matches = [];
                                        let chosenVal = null;
                                        for (let i = 0; i < opts.length; i++) {
                                            const opt = $(opts[i]);
                                            const txt = normalizeText(
                                                opt.text()
                                            );
                                            let ok = true;
                                            for (
                                                let k = 0;
                                                k < required.length;
                                                k++
                                            ) {
                                                const syns = tokenSynonyms(
                                                    required[k]
                                                );
                                                let hasOne = false;
                                                for (
                                                    let s = 0;
                                                    s < syns.length;
                                                    s++
                                                ) {
                                                    if (txt.includes(syns[s])) {
                                                        hasOne = true;
                                                        break;
                                                    }
                                                }
                                                if (!hasOne) {
                                                    ok = false;
                                                    break;
                                                }
                                            }
                                            if (ok) {
                                                matches.push(
                                                    opt.prop("outerHTML")
                                                );
                                                if (!chosenVal)
                                                    chosenVal = opt.val();
                                            }
                                        }
                                        if (matches.length < 2 && primary) {
                                            required = [primary];
                                            const tmp = [];
                                            chosenVal = null;
                                            for (
                                                let i = 0;
                                                i < opts.length;
                                                i++
                                            ) {
                                                const opt = $(opts[i]);
                                                const txt = normalizeText(
                                                    opt.text()
                                                );
                                                const syns =
                                                    tokenSynonyms(primary);
                                                let hasOne = false;
                                                for (
                                                    let s = 0;
                                                    s < syns.length;
                                                    s++
                                                ) {
                                                    if (txt.includes(syns[s])) {
                                                        hasOne = true;
                                                        break;
                                                    }
                                                }
                                                if (hasOne) {
                                                    tmp.push(
                                                        opt.prop("outerHTML")
                                                    );
                                                    if (!chosenVal)
                                                        chosenVal = opt.val();
                                                }
                                            }
                                            if (tmp.length > 0) {
                                                matches.splice(
                                                    0,
                                                    matches.length,
                                                    ...tmp
                                                );
                                            }
                                        }
                                        if (matches.length > 0) {
                                            const parts = [];
                                            if (placeholder.length)
                                                parts.push(
                                                    placeholder.prop(
                                                        "outerHTML"
                                                    )
                                                );
                                            parts.push(matches.join(""));
                                            $model.html(parts.join(""));
                                            // Auto-selection removed as per request
                                            // if (chosenVal) {
                                            //    $model.val(chosenVal).trigger("change");
                                            // }
                                        } else {
                                            $model.html(originalHtml);
                                        }
                                        const ref =
                                            data.refFipe ||
                                            data.marcaModelo ||
                                            data.marcamodelo ||
                                            "";
                                        if (ref) {
                                            modalEl
                                                .find("#referenceModel")
                                                .text(`(${ref})`)
                                                .show();
                                        }
                                    }, 600);
                                    modalEl
                                        .find("#inputYearOfManufacture")
                                        .val(
                                            data.ano && data.ano !== "null"
                                                ? data.ano
                                                : ""
                                        );
                                    modalEl
                                        .find("#inputYearModel")
                                        .val(
                                            data.anoModelo &&
                                                data.anoModelo !== "null"
                                                ? data.anoModelo
                                                : ""
                                        );
                                    modalEl
                                        .find("#inputTypeOfFuel")
                                        .val(
                                            data.extra &&
                                                data.extra.combustivel &&
                                                data.extra.combustivel !==
                                                    "null"
                                                ? data.extra.combustivel
                                                : ""
                                        );
                                    modalEl
                                        .find("#inputChassis")
                                        .val(
                                            data.extra &&
                                                data.extra.chassi &&
                                                data.extra.chassi !== "null"
                                                ? data.extra.chassi
                                                : ""
                                        );
                                    modalEl
                                        .find("#inputCarColor")
                                        .val(
                                            data.cor && data.cor !== "null"
                                                ? data.cor
                                                : ""
                                        );
                                    modalEl
                                        .find("#inputRenavam")
                                        .val(
                                            data.extra &&
                                                data.extra.renavam &&
                                                data.extra.renavam !== "null"
                                                ? data.extra.renavam
                                                : ""
                                        );
                                    modalEl
                                        .find("#inputEngineNumber")
                                        .val(
                                            data.extra &&
                                                data.extra.motor &&
                                                data.extra.motor !== "null"
                                                ? data.extra.motor
                                                : ""
                                        );
                                }
                            },
                            error: function () {
                                const cont = modalEl.find(".modal-body");
                                cont.find(".alert").remove();
                                cont.prepend(
                                    '<div class="alert alert-warning">Não foi possível buscar dados da placa agora.</div>'
                                );
                            },
                        });
                    }, 500);
                }
            });
        });
    }

    formatPhone(phone) {
        const cleaned = (phone || "").replace(/\D/g, "");
        if (cleaned.length === 11)
            return cleaned.replace(
                /(\d{2})(\d{1})(\d{4})(\d{4})/,
                "($1) $2 $3-$4"
            );
        if (cleaned.length === 10)
            return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
        return phone || "";
    }

    createBaseFormModal(title, formHtml, modalId = "quick-create-base-modal") {
        const id = modalId;
        $(`#${id}`).remove();
        const html = `
            <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true" style="z-index: 1060;">
              <div class="modal-dialog modal-lg">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">${title}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"><i class="sg sg-x"></i></button>
                  </div>
                  <div class="modal-body">${formHtml}</div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                    <button type="button" class="btn btn-primary" id="modalBaseSubmit">Salvar</button>
                  </div>
                </div>
              </div>
            </div>`;
        $("body").append(html);
        const el = $(`#${id}`);
        const modal = new bootstrap.Modal(el[0], {
            backdrop: "static",
            keyboard: false,
        });
        modal.show();
        el.on("hidden.bs.modal", function () {
            el.remove();
            // Restore modal-open class if there are other open modals
            if ($('.modal.show').length > 0) {
                $('body').addClass('modal-open');
            }
        });
        return el;
    }

    /**
     * Manipulação de datas
     */
    initDateHandling() {
        const toggleDataEmissao = () => {
            const statusOS = $("#selectStatusOS").val();
            const inputDateIssue = $("#inputDateIssue");
            const dataEmissao = $("#dataEmissao");
            const dataAtual = new Date().toISOString().split("T")[0];

            if (statusOS === "rascunho") {
                inputDateIssue.prop("disabled", true);
                dataEmissao.val("");
                inputDateIssue.val("");
            } else {
                inputDateIssue.prop("disabled", false);
                if (!dataEmissao.val()) {
                    dataEmissao.val(dataAtual);
                    inputDateIssue.val(dataAtual);
                }
            }
        };

        // Executar ao carregar a página
        toggleDataEmissao();

        // Executar quando o status mudar
        $("#selectStatusOS").on("change", toggleDataEmissao);

        // Sincronizar campo visível com campo hidden
        $("#inputDateIssue").on("change", function () {
            $("#dataEmissao").val($(this).val());
        });
    }

    /**
     * Submissão de formulários
     */
    initFormSubmission() {
        $(".formCreateServcieOrder").on("submit", (e) => {
            this.handleFormSubmit(e);
        });
    }

    /**
     * Manipular submissão do formulário
     */
    handleFormSubmit(e) {
        e.preventDefault();
        e.stopPropagation();

        const mainForm = e.target;
        const $form = $(mainForm);
        const currentStatus = $("#selectStatusOS").val();

        // Marcar formulário como em validação
        $form.addClass("was-validated");

        // 1) Validação nativa HTML5
        const htmlValid = mainForm.checkValidity();

        // 2) Validação condicional dos campos obrigatórios por status
        const customValid = this.validateRequiredFieldsByStatus(currentStatus);

        // Se qualquer validação falhar, focar no primeiro erro e não enviar
        if (!htmlValid || !customValid) {
            const $firstInvalid = $form.find(".is-invalid, :invalid").first();
            if ($firstInvalid.length) {
                try {
                    const top = $firstInvalid.offset().top - 120;
                    window.scrollTo({ top, behavior: "smooth" });
                    $firstInvalid.focus();
                } catch (err) {}
            }
            return;
        }

        // Adicionar dados das NFs ao formulário usando o NfManager
        if (this.nfManager && this.nfManager.nfsData) {
            this.nfManager.nfsData.forEach((nf, index) => {
                $(mainForm).append(
                    `<input type="hidden" name="nfs[${index}][tipo]" value="${nf.tipo}">`
                );
                $(mainForm).append(
                    `<input type="hidden" name="nfs[${index}][numero_nota]" value="${nf.numero_nota}">`
                );
                $(mainForm).append(
                    `<input type="hidden" name="nfs[${index}][serie]" value="${nf.serie}">`
                );
                $(mainForm).append(
                    `<input type="hidden" name="nfs[${index}][data_emissao]" value="${nf.data_emissao}">`
                );
                $(mainForm).append(
                    `<input type="hidden" name="nfs[${index}][valor_total]" value="${nf.valor_total}">`
                );
                $(mainForm).append(
                    `<input type="hidden" name="nfs[${index}][observacoes]" value="${
                        nf.observacoes || ""
                    }">`
                );

                if (nf.arquivo) {
                    $(mainForm).append(
                        `<input type="hidden" name="nfs[${index}][tipo_arquivo]" value="${nf.tipo_arquivo}">`
                    );
                    $(mainForm).append(
                        `<input type="hidden" name="nfs[${index}][nome_arquivo]" value="${nf.nome_arquivo}">`
                    );
                    $(mainForm).append(
                        `<input type="hidden" name="nfs[${index}][arquivo_base64]" value="${nf.arquivo}">`
                    );
                }
            });
        }

        // Verificar se precisa de confirmação WhatsApp (apenas para create)
        if (
            this.isCreate &&
            $("#whatsappConfirmation").is(":checked") &&
            !this.submitConfirmed
        ) {
            this.handleWhatsAppConfirmation(mainForm);
            return;
        }

        // Submeter o formulário
        // Garantir persistência imediata dos itens antes do envio
        if (typeof this.saveItemsToStorage === "function") {
            this.saveItemsToStorage();
        }
        this.isSubmitting = true;
        this.isFormDirty = false;
        mainForm.submit();
    }

    /**
     * Manipular confirmação WhatsApp (específico para create)
     */
    handleWhatsAppConfirmation(mainForm) {
        const clientName = $("#inputClientName").val() || "Cliente";
        const vehicleInfo = $("#inputVehiclePlate").val() || "Veículo";

        const message = `Confirmar envio de WhatsApp para ${clientName} sobre ${vehicleInfo}?`;

        if (confirm(message)) {
            this.submitConfirmed = true;
            $(mainForm).trigger("submit");
        }
    }

    /**
     * Configurar autocomplete
     */
    setupAutocomplete() {
        if (this.autocompleteManager) {
            // Configurações específicas de autocomplete podem ser adicionadas aqui
        }
    }

    /**
     * Configurar validação de formulários
     */
    setupFormValidation() {
        const forms = document.querySelectorAll(".needs-validation");
        Array.from(forms).forEach((form) => {
            form.addEventListener(
                "submit",
                (event) => {
                    if (!form.checkValidity()) {
                        event.preventDefault();
                        event.stopPropagation();
                    }
                    form.classList.add("was-validated");
                },
                false
            );
        });
    }

    /**
     * Calcular totais do formulário
     */
    calculateTotals() {
        const self = this;
        let totalProducts = 0;
        let totalServices = 0;

        // Acumuladores para rateio
        const productItems = [];
        const serviceItems = [];

        // Calcular total de produtos com descontos individuais e lucro líquido por item
        $(".tbodyProducts tr").each(function () {
            const $row = $(this);
            const qty = parseFloat($row.find(".qtdItem").val()) || 0;
            const price =
                parseFloat(
                    $row
                        .find(".priceItem")
                        .val()
                        .replace(/[^\d,]/g, "")
                        .replace(",", ".")
                ) || 0;
            const cost =
                parseFloat(
                    ($row.find(".costPrice").val() || "0")
                        .replace(/[^\d,]/g, "")
                        .replace(",", ".")
                ) || 0;
            const hash = $row.find(".discount-input").data("hash");

            let subtotal = qty * price;
            let discount = 0;

            // Verificar desconto individual
            if (hash) {
                const discountValue =
                    parseFloat($(`#discountByValue${hash}`).val()) || 0;
                const discountPercent =
                    parseFloat($(`#discount${hash}`).val()) || 0;

                if (discountValue > 0) {
                    discount = discountValue;
                } else if (discountPercent > 0) {
                    discount = (subtotal * discountPercent) / 100;
                }
            }

            const lineTotal = subtotal - discount; // total da linha antes de descontos gerais
            $row.find(".totalValueProduct").val(lineTotal.toFixed(2));
            $row.find('input[id^="totalValue"]:not(.totalValueProduct)').val(
                lineTotal.toFixed(2).replace(".", ",")
            );
            // Registrar item para rateio
            productItems.push({
                $row,
                qty,
                price,
                cost,
                lineBeforeGeneral: lineTotal,
            });
            totalProducts += lineTotal;
        });

        // Calcular total de serviços com descontos individuais e lucro líquido por item
        $(".tbodyOSServices tr").each(function () {
            const $row = $(this);
            const qty = parseFloat($row.find(".qtdItem").val()) || 0;
            const priceText = $row.find(".priceItem").val() || "0";
            const price =
                parseFloat(
                    priceText.replace(/[^\d,]/g, "").replace(",", ".")
                ) || 0;
            const cost = 0;
            const hash = $row.find(".discount-input").data("hash");

            let subtotal = qty * price;
            let discount = 0;

            // Verificar desconto individual
            if (hash) {
                const discountValue =
                    parseFloat($(`#discountByValue${hash}`).val()) || 0;
                const discountPercent =
                    parseFloat($(`#discount${hash}`).val()) || 0;

                if (discountValue > 0) {
                    discount = discountValue;
                } else if (discountPercent > 0) {
                    discount = (subtotal * discountPercent) / 100;
                }
            }

            const lineTotal = subtotal - discount; // total da linha antes de descontos gerais
            $row.find(".totalValueService").val(lineTotal.toFixed(2));

            // Atualizar campo visual do total com formatação
            const $totalDisplay = $row.find(
                'input[id^="totalValue"]:not(.totalValueService)'
            );
            $totalDisplay.val(lineTotal.toFixed(2).replace(".", ","));

            // Registrar item para rateio
            serviceItems.push({
                $row,
                qty,
                price,
                cost,
                lineBeforeGeneral: lineTotal,
            });

            totalServices += lineTotal;
        });

        // Aplicar descontos gerais
        let discountProducts = 0;
        let discountServices = 0;

        // Desconto geral de produtos
        const discountProductsValue =
            parseFloat($("#discountByValueProducts").val()) || 0;
        const discountProductsPercent =
            parseFloat($("#discountAllProducts").val()) || 0;

        if (discountProductsValue > 0) {
            discountProducts = discountProductsValue;
        } else if (discountProductsPercent > 0) {
            discountProducts = (totalProducts * discountProductsPercent) / 100;
        }

        // Desconto geral de serviços
        const discountServicesValue =
            parseFloat($("#discountByValueServices").val()) || 0;
        const discountServicesPercent =
            parseFloat($("#discountAllServices").val()) || 0;

        if (discountServicesValue > 0) {
            discountServices = discountServicesValue;
        } else if (discountServicesPercent > 0) {
            discountServices = (totalServices * discountServicesPercent) / 100;
        }

        // Aplicar descontos gerais aos subtotais
        const productsAfterGeneral = Math.max(
            0,
            totalProducts - discountProducts
        );
        const servicesAfterGeneral = Math.max(
            0,
            totalServices - discountServices
        );
        totalProducts = productsAfterGeneral;
        totalServices = servicesAfterGeneral;

        // Rateio do desconto geral de produtos entre itens
        if (discountProducts > 0 && totalProducts + discountProducts > 0) {
            const baseProducts = productItems.reduce(
                (acc, it) => acc + it.lineBeforeGeneral,
                0
            );
            if (baseProducts > 0) {
                productItems.forEach((it) => {
                    const part =
                        (it.lineBeforeGeneral / baseProducts) *
                        discountProducts;
                    it.allocatedGeneral = part;
                });
            } else {
                productItems.forEach((it) => {
                    it.allocatedGeneral = 0;
                });
            }
        } else {
            productItems.forEach((it) => {
                it.allocatedGeneral = 0;
            });
        }

        // Rateio do desconto geral de serviços entre itens
        if (discountServices > 0 && totalServices + discountServices > 0) {
            const baseServices = serviceItems.reduce(
                (acc, it) => acc + it.lineBeforeGeneral,
                0
            );
            if (baseServices > 0) {
                serviceItems.forEach((it) => {
                    const part =
                        (it.lineBeforeGeneral / baseServices) *
                        discountServices;
                    it.allocatedGeneral = part;
                });
            } else {
                serviceItems.forEach((it) => {
                    it.allocatedGeneral = 0;
                });
            }
        } else {
            serviceItems.forEach((it) => {
                it.allocatedGeneral = 0;
            });
        }

        // Atualizar subtotais na interface
        $(".subtotalProducts").text(
            "R$ " + totalProducts.toFixed(2).replace(".", ",")
        );
        $(".subtotalServices").text(
            "R$ " + totalServices.toFixed(2).replace(".", ",")
        );
        $("#subTotalProducts").val(totalProducts.toFixed(2));
        $("#subTotalServices").val(totalServices.toFixed(2));

        // Calcular total geral
        let totalGeneral = totalProducts + totalServices;

        // Aplicar desconto total se houver
        const discountTotalValue =
            parseFloat($("#fullDiscountByValue").val()) || 0;
        const discountTotalPercent = parseFloat($("#fullDiscount").val()) || 0;

        let fullDiscount = 0;
        if (discountTotalValue > 0) {
            fullDiscount = discountTotalValue;
        } else if (discountTotalPercent > 0) {
            fullDiscount = (totalGeneral * discountTotalPercent) / 100;
        }
        totalGeneral = Math.max(0, totalGeneral - fullDiscount);

        // Rateio do desconto total entre todos os itens (após descontos gerais de categoria)
        const baseForFull = productsAfterGeneral + servicesAfterGeneral;
        if (fullDiscount > 0 && baseForFull > 0) {
            // Primeiro calcular o valor por item após desconto geral da categoria
            productItems.forEach((it) => {
                it.afterGeneral =
                    it.lineBeforeGeneral - (it.allocatedGeneral || 0);
            });
            serviceItems.forEach((it) => {
                it.afterGeneral =
                    it.lineBeforeGeneral - (it.allocatedGeneral || 0);
            });

            const sumAfterGeneral =
                productItems.reduce((a, it) => a + it.afterGeneral, 0) +
                serviceItems.reduce((a, it) => a + it.afterGeneral, 0);
            const base = sumAfterGeneral > 0 ? sumAfterGeneral : baseForFull;

            productItems.forEach((it) => {
                const part = (it.afterGeneral / base) * fullDiscount;
                it.allocatedFull = part;
            });
            serviceItems.forEach((it) => {
                const part = (it.afterGeneral / base) * fullDiscount;
                it.allocatedFull = part;
            });
        } else {
            productItems.forEach((it) => {
                it.allocatedFull = 0;
            });
            serviceItems.forEach((it) => {
                it.allocatedFull = 0;
            });
        }

        // Atualizar lucro_liquido por item com rateio aplicado
        productItems.forEach((it) => {
            const revenue = Math.max(
                0,
                it.lineBeforeGeneral -
                    (it.allocatedGeneral || 0) -
                    (it.allocatedFull || 0)
            );
            const netProfit = revenue - it.qty * it.cost;
            it.$row.find(".lucroLiquidoProduct").val(netProfit.toFixed(2));
        });
        serviceItems.forEach((it) => {
            const revenue = Math.max(
                0,
                it.lineBeforeGeneral -
                    (it.allocatedGeneral || 0) -
                    (it.allocatedFull || 0)
            );
            const netProfit = revenue - it.qty * it.cost;
            it.$row.find(".lucroLiquidoService").val(netProfit.toFixed(2));
        });

        // Atualizar total geral na interface
        $(".totalValueOS").text(
            "R$ " + totalGeneral.toFixed(2).replace(".", ",")
        );
        $(".valueFinalOS").text(
            "R$ " + totalGeneral.toFixed(2).replace(".", ",")
        );
        $("#valueFinalOS").val(totalGeneral.toFixed(2));
        $("#valueOriginalOS").val((totalProducts + totalServices).toFixed(2));

        // Disparar evento para outros módulos
        $(document).trigger("totalsCalculated", {
            products: totalProducts,
            services: totalServices,
            total: totalGeneral,
        });
    }

    /**
     * Configurar event listeners gerais
     */
    setupEventListeners() {
        const self = this;

        // Remover event listeners existentes para evitar duplicação
        $(document).off("click.serviceOrderForm");
        $(document).off("input.serviceOrderForm");

        // Eventos para botões de adicionar produto
        $(document).on("click.serviceOrderForm", ".button-add-product", (e) => {
            e.preventDefault();
            e.stopPropagation();
            self.addProductRow();
        });

        // Eventos para botões de adicionar serviço
        $(document).on("click.serviceOrderForm", ".button-add-service", (e) => {
            e.preventDefault();
            e.stopPropagation();
            self.addServiceRow();
        });

        // Eventos para botões de remover produto
        $(document).on(
            "click.serviceOrderForm",
            ".button-remove-product",
            (e) => {
                e.preventDefault();
                e.stopPropagation();
                const row = $(e.target).closest("tr");
                row.remove();
                self.calculateTotals();

                // Disparar evento adicional para garantir atualização
                setTimeout(function () {
                    self.calculateTotals();
                    self.saveItemsToStorage();
                }, 50);
            }
        );

        // Eventos para campos de desconto individuais
        $(document).on(
            "input.serviceOrderForm",
            ".discount-input[data-hash]",
            function () {
                const $input = $(this);
                const hash = $input.data("hash");
                const valor = $input.val();
                const isValue = $input.hasClass("valorMask");

                if (!hash) return;

                if (valor && valor.trim() !== "") {
                    if (isValue) {
                        const valorLimpo = valor
                            .replace(/[^\d,]/g, "")
                            .replace(",", ".");
                        $(`#discountByValue${hash}`).val(valorLimpo);
                        $(`#discount${hash}`).val("");
                    } else {
                        const percentLimpo = valor
                            .replace("%", "")
                            .replace(",", ".");
                        $(`#discount${hash}`).val(percentLimpo);
                        $(`#discountByValue${hash}`).val("");
                    }
                } else {
                    // Garantir limpeza completa quando campo está vazio
                    $(`#discountByValue${hash}`).val("");
                    $(`#discount${hash}`).val("");
                }

                // Aguardar um momento antes de recalcular para garantir que todos os campos foram atualizados
                setTimeout(function () {
                    self.calculateTotals();
                }, 10);
            }
        );

        // Eventos para botões de remover serviço
        $(document).on(
            "click.serviceOrderForm",
            ".button-remove-service",
            (e) => {
                e.preventDefault();
                e.stopPropagation();
                const row = $(e.target).closest("tr");
                row.remove();
                self.calculateTotals();
                self.saveItemsToStorage();
            }
        );

        // Eventos para recálculo automático
        $(document).on(
            "input.serviceOrderForm",
            ".qtdItem, .priceItem, .discount-input, .valorVenda",
            function () {
                self.calculateTotals();
                self.saveItemsToStorage();
            }
        );

        // Persistir qualquer alteração nos campos de itens (produtos/serviços)
        $(document).on(
            "input.serviceOrderForm change.serviceOrderForm",
            ".tbodyProducts input, .tbodyOSServices input, .tbodyOSServices select",
            function () {
                self.saveItemsToStorage();
            }
        );

        // Sincronizar campo Cód. visível com hidden [codigo] nos serviços
        $(document).on(
            "input.serviceOrderForm",
            ".tbodyOSServices input[id^='cod']",
            function () {
                const id = $(this).attr("id");
                const hash = id ? id.replace("cod", "") : null;
                if (hash && $(`#codigo${hash}`).length) {
                    $(`#codigo${hash}`).val($(this).val() || "");
                }
            }
        );

        // Habilitar/Desabilitar campo de Parcelamento conforme forma de pagamento
        $(document).on(
            "change.serviceOrderForm",
            ".inputPaymentMethod",
            function () {
                const method = $(this).val();
                const hash = $(this).data("hash") || null;

                let $installments = hash
                    ? $(`#inputInstallments${hash}`)
                    : $("#inputInstallments");

                // Fallback: buscar no formulário atual caso ID não encontrado
                if (!$installments.length) {
                    const $form = $(this).closest("form");
                    $installments = $form.find(
                        "#inputInstallments, [id^='inputInstallments']"
                    );
                }

                if (method === "credito") {
                    $installments
                        .prop("disabled", false)
                        .attr("required", true);
                    if (!$installments.val()) {
                        $installments.val("1");
                    }
                } else {
                    $installments
                        .prop("disabled", true)
                        .removeAttr("required")
                        .val("");
                }
            }
        );

        // Sincronizar valor editado do pagamento com campo hidden enviado ao backend
        $(document).on(
            "input.serviceOrderForm change.serviceOrderForm",
            ".inputTotalValueEdit",
            function () {
                const $input = $(this);
                const hash = $input.data("hash");
                if (!hash) return;

                // Valor digitado com máscara BR (ex.: 1.234,56)
                const maskedValue = ($input.val() || "").trim();
                // Converter para float para validação de saldo
                const numericValue =
                    parseFloat(
                        maskedValue.replace(/[^\d,]/g, "").replace(",", ".")
                    ) || 0;

                // Atualizar campo hidden que é realmente enviado ao backend
                const $hidden = $(`#currentValue${hash}`);
                if ($hidden.length) {
                    // Enviar o mesmo formato que o usuário digitou; backend converte corretamente
                    $hidden.val(maskedValue);
                }

                // Validação: não permitir valor maior que saldo devedor
                const $outstanding = $(`#updatedOutstandingBalance${hash}`);
                const outstanding = $outstanding.length
                    ? parseFloat(
                          ($outstanding.val() || "0")
                              .toString()
                              .replace(",", ".")
                      )
                    : 0;

                const $invalid = $input
                    .closest(".col-md-4")
                    .find(".invalid-value-edit");
                const $submitBtn = $input
                    .closest("form")
                    .find(".btn-editaPagamentoDB");

                if (numericValue > outstanding && outstanding > 0) {
                    $invalid.show();
                    $submitBtn.prop("disabled", true);
                } else {
                    $invalid.hide();
                    $submitBtn.prop("disabled", false);
                }
            }
        );

        // Bloquear submissão se o valor exceder o saldo devedor
        $(document).on(
            "submit.serviceOrderForm",
            "form[id^='formPaymentOs']",
            function (e) {
                const $form = $(this);
                const $invalid = $form.find(".invalid-value-edit:visible");
                if ($invalid.length) {
                    e.preventDefault();
                    e.stopPropagation();
                    // Garantir feedback ao usuário
                    const $btn = $form.find(".btn-editaPagamentoDB");
                    $btn.prop("disabled", true);
                }
            }
        );

        // Inicializar estado dos selects ao carregar a página
        $(".inputPaymentMethod").each(function () {
            $(this).trigger("change");
        });

        // Eventos para seleção de produtos
        $(document).on(
            "select2:select.serviceOrderForm",
            ".selectProduct",
            function (e) {
                const data = e.params.data;
                const hash = $(this).data("hash");

                if (data && data.id) {
                    // Preencher campos do produto
                    $(`#codigo${hash}`).val(data.codigo || "");
                    $(`#price${hash}`).val(data.preco || "0,00");
                    $(`#originalProductValue${hash}`).val(data.preco || "0,00");

                    // Recalcular totais
                    self.calculateTotals();
                    self.saveItemsToStorage();
                }
            }
        );

        // Eventos para seleção de serviços
        $(document).on(
            "select2:select.serviceOrderForm",
            ".selectService",
            function (e) {
                const data = e.params.data;
                const hash = $(this).data("hash");
                const $select = $(this);

                if (data && data.id) {
                    // Se for uma tag personalizada (texto livre)
                    if (data.newTag) {
                        // Para tags personalizadas, definir o valor como o texto digitado
                        $select.val(data.text).trigger("change");

                        // Mapear como serviço novo: limpar id e setar nome
                        $(`#id${hash}`).val("");
                        $(`#nome${hash}`).val(data.text);

                        // Limpar/sincronizar código e preços
                        $(`#codigo${hash}`).val("");
                        $(`#cod${hash}`).prop("disabled", true).val("");
                        $(`#price${hash}`).val("");
                        $(`#originalServiceValue${hash}`).val("");

                    } else {
                        // Para serviços existentes, definir o valor como o ID do serviço
                        // O Select2 automaticamente exibirá o texto correspondente
                        $select.val(data.id).trigger("change");

                        // Mapear como serviço existente: setar id e limpar nome, bloquear código
                        $(`#id${hash}`).val(data.id);
                        $(`#nome${hash}`).val("");
                        $(`#cod${hash}`).prop("disabled", true);
                        $(`#codigo${hash}`).val(data.codigo || "");

                        // Verificar se os dados do serviço estão disponíveis no objeto data
                        if (
                            data.codigo !== undefined &&
                            data.valor !== undefined
                        ) {
                            // Dados disponíveis via AJAX
                            $(`#cod${hash}`).val(data.codigo || "");

                            // Verificar se o valor não é null ou zero antes de preencher
                            const valorServico = data.valor;
                            const valorNumerico = valorServico
                                ? parseFloat(
                                      valorServico.toString().replace(",", ".")
                                  )
                                : null;

                            if (valorServico && valorNumerico > 0) {
                                $(`#price${hash}`).val(valorServico);
                                $(`#originalServiceValue${hash}`).val(
                                    valorServico
                                );
                            }
                        } else {
                            // Para opções pré-existentes (edição), buscar dados dos campos hidden
                            const serviceId = data.id;
                            const $hiddenCodigo = $(`#codigo${hash}`);
                            const $originalValue = $(
                                `#originalServiceValue${hash}`
                            );

                            if ($hiddenCodigo.length && $originalValue.length) {
                                const codigo = $hiddenCodigo.val();
                                const originalText =
                                    $originalValue.data("original-text") || "";

                                // Extrair o valor do texto original se disponível
                                let valor = "0,00";
                                const $priceField = $(`#price${hash}`);
                                if ($priceField.length && $priceField.val()) {
                                    valor = $priceField.val();
                                }

                                $(`#cod${hash}`).val(codigo || "");

                                // Verificar se o valor não é null ou zero antes de preencher
                                const valorNumerico = valor
                                    ? parseFloat(
                                          valor.toString().replace(",", ".")
                                      )
                                    : null;

                                if (valor && valorNumerico > 0) {
                                    $(`#price${hash}`).val(valor);
                                    $(`#originalServiceValue${hash}`).val(
                                        valor
                                    );
                                }
                            }
                        }

                    }

                    // Recalcular totais
                    self.calculateTotals();
                    self.saveItemsToStorage();
                }
            }
        );

        // Função global para selecionar serviço no Select2 após cadastro
        window.selectServiceInSelect2 = function (service, hash) {
            if (!hash || !service) return;

            const $select = $(`#selectService${hash}`);
            if (
                $select.length &&
                $select.hasClass("select2-hidden-accessible")
            ) {
                // Criar nova opção
                const newOption = new Option(
                    service.nome_servico || service.nome,
                    service.id,
                    true,
                    true
                );
                $select.append(newOption).trigger("change");

                // Preencher campos relacionados
                $(`#cod${hash}`).val(service.codigo || "");

                // Formatar valores corretamente para pt-BR
                const valorServico =
                    service.valor_servico || service.valor || "0";
                const valorFormatado = parseFloat(
                    valorServico.toString().replace(",", ".")
                )
                    .toFixed(2)
                    .replace(".", ",");

                $(`#price${hash}`).val(valorFormatado);
                $(`#originalServiceValue${hash}`).val(valorFormatado);

                // Recalcular totais
                if (typeof self.calculateTotals === "function") {
                    self.calculateTotals();
                }
            }
        };

        // Eventos para botões de switch de desconto individuais
        $(document).on(
            "click.serviceOrderForm",
            ".switch-select__btn[data-hash]",
            function (e) {
                e.preventDefault();
                const $btn = $(this);
                const $container = $btn.closest(".switch-select");
                const hash = $btn.data("hash");
                const target = $btn.data("target") || $btn.data("type"); // Suporte para ambos os atributos
                const $input = $(`#discountInput${hash}`);

                if (!hash || !$input.length) return;

                // Remove active de todos os botões do container
                $container
                    .find(".switch-select__btn")
                    .removeClass("switch-select__btn--active");
                // Adiciona active ao botão clicado
                $btn.addClass("switch-select__btn--active");

                // Remover máscara antes de limpar
                if (typeof $input.unmask === "function") {
                    try {
                        $input.unmask();
                    } catch (e) {}
                }

                // Limpar valor atual completamente - múltiplas abordagens para garantir limpeza
                $input.val("").removeAttr("value").prop("value", "");

                if (target === "value") {
                    $input.removeClass("percent").addClass("valorMask");
                    $input.attr("placeholder", "0,00");
                    // Reinicializar máscara de valor com verificação
                    if (typeof $input.mask === "function") {
                        try {
                            $input.mask("#.##0,00", {
                                reverse: true,
                                placeholder: ",00",
                            });
                        } catch (e) {}
                    }
                } else {
                    $input.removeClass("valorMask").addClass("percent");
                    $input.attr("placeholder", "0%");
                    // Reinicializar máscara de percentual com verificação
                    if (typeof $input.mask === "function") {
                        try {
                            $input.mask("##0%", {
                                reverse: true,
                                placeholder: "0%",
                            });
                        } catch (e) {}
                    }
                }

                // Limpar campos ocultos e forçar atualização
                $(`#discountByValue${hash}`).val("").trigger("change");
                $(`#discount${hash}`).val("").trigger("change");

                // Aguardar um momento para garantir que as máscaras foram aplicadas
                setTimeout(function () {
                    // Garantir limpeza completa dos campos hidden novamente
                    $(`#discountByValue${hash}`).val("");
                    $(`#discount${hash}`).val("");

                    // Limpar novamente o campo visível para garantir que não há resíduos
                    $input.val("").removeAttr("value").prop("value", "");

                    // Disparar evento de input após a limpeza completa
                    try {
                        $input.trigger("input");
                    } catch (e) {}

                    // Recalcular totais
                    self.calculateTotals();
                }, 150);
            }
        );

        // Eventos para botões de switch de desconto gerais (produtos, serviços, total)
        $(document).on(
            "click.serviceOrderForm",
            ".switch-select__btn[data-target]:not([data-hash])",
            function (e) {
                e.preventDefault();
                const $btn = $(this);
                const $container = $btn.closest(".switch-select");
                const target = $btn.data("target");

                let $input;
                if (target === "products") {
                    $input = $("#discountInputProducts");
                } else if (target === "services") {
                    $input = $("#discountInputServices");
                } else if (target === "total") {
                    $input = $("#discountTotalInput");
                }

                if (!$input || !$input.length) return;

                // Remove active de todos os botões do container
                $container
                    .find(".switch-select__btn")
                    .removeClass("switch-select__btn--active");
                // Adiciona active ao botão clicado
                $btn.addClass("switch-select__btn--active");

                // Limpar valor atual
                $input.val("");

                if ($btn.data("type") === "value") {
                    $input.removeClass("percent").addClass("valorMask");
                    $input.attr("placeholder", "0,00");
                    // Reinicializar máscara de valor
                    if (typeof $input.unmask === "function") {
                        $input.unmask();
                    }
                    if (typeof $input.mask === "function") {
                        $input.mask("#.##0,00", {
                            reverse: true,
                            placeholder: "0,00",
                        });
                    }
                } else {
                    $input.removeClass("valorMask").addClass("percent");
                    $input.attr("placeholder", "0%");
                    // Reinicializar máscara de percentual
                    if (typeof $input.unmask === "function") {
                        $input.unmask();
                    }
                    if (typeof $input.mask === "function") {
                        $input.mask("##0%", {
                            reverse: true,
                            placeholder: "0%",
                        });
                    }
                }

                // Limpar campo visível primeiro
                $input.val("").trigger("input");

                // Limpar campos ocultos correspondentes e forçar atualização
                if (target === "products") {
                    $("#discountByValueProducts").val("").trigger("change");
                    $("#discountAllProducts").val("").trigger("change");
                } else if (target === "services") {
                    $("#discountByValueServices").val("").trigger("change");
                    $("#discountAllServices").val("").trigger("change");
                } else if (target === "total") {
                    $("#fullDiscountByValue").val("").trigger("change");
                    $("#fullDiscount").val("").trigger("change");
                }

                // Forçar atualização visual do campo
                $input.focus().blur();

                // Aguardar um momento para garantir que as máscaras foram aplicadas e campos limpos
                setTimeout(function () {
                    // Garantir limpeza completa dos campos hidden novamente
                    if (target === "products") {
                        $("#discountByValueProducts").val("");
                        $("#discountAllProducts").val("");
                    } else if (target === "services") {
                        $("#discountByValueServices").val("");
                        $("#discountAllServices").val("");
                    } else if (target === "total") {
                        $("#fullDiscountByValue").val("");
                        $("#fullDiscount").val("");
                    }

                    // Recalcular totais
                    self.calculateTotals();

                    // Disparar evento personalizado para notificar outros módulos
                    $(document).trigger("discountToggled", {
                        target: target,
                        type: $btn.data("type"),
                        input: $input,
                    });
                }, 100);
            }
        );

        // Eventos para campos de desconto geral
        $(document).on(
            "input.serviceOrderForm",
            "#discountInputProducts, #discountInputServices, #discountTotalInput, .discount-input-general, .discount-input-total",
            function () {
                const $input = $(this);
                const valor = $input.val();
                const isValue = $input.hasClass("valorMask");

                if ($input.attr("id") === "discountInputProducts") {
                    if (valor && valor.trim() !== "") {
                        if (isValue) {
                            const valorLimpo = valor
                                .replace(/[^\d,]/g, "")
                                .replace(",", ".");
                            $("#discountByValueProducts").val(valorLimpo);
                            $("#discountAllProducts").val("");
                        } else {
                            const percentLimpo = valor
                                .replace("%", "")
                                .replace(",", ".");
                            $("#discountAllProducts").val(percentLimpo);
                            $("#discountByValueProducts").val("");
                        }
                    } else {
                        // Garantir limpeza completa quando campo está vazio
                        $("#discountByValueProducts").val("");
                        $("#discountAllProducts").val("");
                    }
                } else if ($input.attr("id") === "discountInputServices") {
                    if (valor && valor.trim() !== "") {
                        if (isValue) {
                            const valorLimpo = valor
                                .replace(/[^\d,]/g, "")
                                .replace(",", ".");
                            $("#discountByValueServices").val(valorLimpo);
                            $("#discountAllServices").val("");
                        } else {
                            const percentLimpo = valor
                                .replace("%", "")
                                .replace(",", ".");
                            $("#discountAllServices").val(percentLimpo);
                            $("#discountByValueServices").val("");
                        }
                    } else {
                        // Garantir limpeza completa quando campo está vazio
                        $("#discountByValueServices").val("");
                        $("#discountAllServices").val("");
                    }
                } else if ($input.attr("id") === "discountTotalInput") {
                    if (valor && valor.trim() !== "") {
                        if (isValue) {
                            const valorLimpo = valor
                                .replace(/[^\d,]/g, "")
                                .replace(",", ".");
                            $("#fullDiscountByValue").val(valorLimpo);
                            $("#fullDiscount").val("");
                        } else {
                            const percentLimpo = valor
                                .replace("%", "")
                                .replace(",", ".");
                            $("#fullDiscount").val(percentLimpo);
                            $("#fullDiscountByValue").val("");
                        }
                    } else {
                        // Garantir limpeza completa quando campo está vazio
                        $("#fullDiscountByValue").val("");
                        $("#fullDiscount").val("");
                    }
                }

                // Aguardar um momento antes de recalcular para garantir que todos os campos foram atualizados
                setTimeout(function () {
                    self.calculateTotals();
                    self.saveItemsToStorage();
                }, 10);
            }
        );
    }

    /**
     * Adicionar nova linha de produto
     */
    addProductRow() {
        const hash = this.generateHash();
        const index = $(".tbodyProducts tr").length;

        const newRow = `
            <tr>
                <td class="ps-0">
                    <input type="hidden" id="id${hash}" name="produtos[${index}][id]" value="">
                    <input type="hidden" id="codigo${hash}" name="produtos[${index}][codigo]" value="" disabled>
                    <input type="text" class="form-control" id="productName${hash}" name="produtos[${index}][nome]" placeholder="Digite o nome do produto" data-hash="${hash}">
                </td>
                <td>
                    <input type="text" class="form-control" id="cod${hash}" name="produtos[${index}][codigo]" value="" placeholder="Código" disabled>
                </td>
                <td>
                    <input type="number" class="form-control qtdItem" id="qtd${hash}" name="produtos[${index}][qtd]" data-hash="${hash}" value="1" min="1" placeholder="1">
                </td>
                <td>
                    <input type="text" class="form-control valorMask costPrice" data-hash="${hash}" name="produtos[${index}][custo]" value="" placeholder="0,00">
                </td>
                <td>
                    <input type="text" class="form-control valorMask priceItem" id="price${hash}" name="produtos[${index}][preco]" data-hash="${hash}" value="" placeholder="0,00">
                </td>
                <td>
                    <div class="input-switch-select">
                        <input type="text" class="form-control discount-input valorMask" id="discountInput${hash}" data-hash="${hash}" value="" placeholder="0,00">
                        <div class="switch-select">
                            <button type="button" class="switch-select__btn switch-select__btn--active" data-target="value" data-hash="${hash}"><i class="sg sg-currency-dollar"></i></button>
                            <button type="button" class="switch-select__btn" data-target="percent" data-hash="${hash}"><i class="sg sg-percent"></i></button>
                        </div>
                    </div>
                    <input type="hidden" class="discountValueItem" id="discountByValue${hash}" name="produtos[${index}][descontoPorValor]" data-hash="${hash}" value="">
                    <input type="hidden" class="discountItem" id="discount${hash}" name="produtos[${index}][desconto]" data-hash="${hash}" value="">
                </td>
                <td>
                    <input type="text" class="form-control" id="totalValue${hash}" value="R$ 0,00" disabled>
                    <input type="hidden" class="totalValueProduct" id="valorTotal${hash}" name="produtos[${index}][valorTotal]" value="0">
                    <input type="hidden" class="lucroLiquidoProduct" id="lucroLiquido${hash}" name="produtos[${index}][lucro_liquido]" value="0">
                </td>
                <td class="text-end pe-0">
                    <button type="button" class="btn btn-icon button-remove-product">
                        <i class="sg sg-trash"></i>
                    </button>
                </td>
            </tr>
        `;

        $(".tbodyProducts").append(newRow);

        // Aplicar máscaras monetárias aos novos campos
        const $newRow = $(".tbodyProducts tr").last();
        $newRow.find(".valorMask").mask("000.000.000.000.000,00", {
            reverse: true,
            placeholder: "0,00",
        });

        // Produtos usam autocomplete, não Select2
        // O autocomplete é inicializado automaticamente pelo sistema de autocomplete
        this.saveItemsToStorage();
        return hash;
    }

    /**
     * Adicionar nova linha de serviço
     */
    addServiceRow() {
        const hash = this.generateHash();
        const index = $(".tbodyOSServices tr").length;

        const newRow = `
            <tr>
                <td class="ps-0">
                    <input type="hidden" id="id${hash}" name="servicos[${index}][id]" value="">
                    <input type="hidden" id="nome${hash}" name="servicos[${index}][nome]" value="">
                    <input type="hidden" id="codigo${hash}" name="servicos[${index}][codigo]" value="" disabled>
                    <input type="hidden" id="originalServiceValue${hash}" value="" data-original-text="">
                    <select class="form-control selectService" id="selectService${hash}" data-hash="${hash}">
                        <option value="">Selecione um serviço</option>
                        ${this.generateServiceOptions()}
                    </select>
                </td>
                <td>
                    <input type="text" class="form-control" id="cod${hash}" value="" placeholder="Código" disabled>
                </td>
                <td>
                    <input type="number" class="form-control qtdItem" id="qtd${hash}" name="servicos[${index}][qtd]" data-hash="${hash}" value="1" min="1" placeholder="1">
                </td>
                <td>
                    <input type="text" class="form-control valorMask priceItem" id="price${hash}" name="servicos[${index}][preco]" data-hash="${hash}" value="" placeholder="0,00">
                </td>
                <td>
                    <div class="input-switch-select">
                        <input type="text" class="form-control discount-input valorMask" id="discountInput${hash}" data-hash="${hash}" value="" placeholder="0,00">
                        <div class="switch-select">
                            <button type="button" class="switch-select__btn switch-select__btn--active" data-target="value" data-hash="${hash}"><i class="sg sg-currency-dollar"></i></button>
                            <button type="button" class="switch-select__btn" data-target="percent" data-hash="${hash}"><i class="sg sg-percent"></i></button>
                        </div>
                    </div>
                    <input type="hidden" class="discountValueItem" id="discountByValue${hash}" name="servicos[${index}][descontoPorValor]" data-hash="${hash}" value="">
                    <input type="hidden" class="discountItem" id="discount${hash}" name="servicos[${index}][desconto]" data-hash="${hash}" value="">
                </td>
                <td>
                    <input type="text" class="form-control" id="totalValue${hash}" value="R$ 0,00" disabled>
                    <input type="hidden" class="totalValueService" id="valorTotal${hash}" name="servicos[${index}][valorTotal]" value="0">
                    <input type="hidden" class="lucroLiquidoService" id="lucroLiquido${hash}" name="servicos[${index}][lucro_liquido]" value="0">
                </td>
                <td class="text-end pe-0">
                    <button type="button" class="btn btn-icon button-remove-service">
                        <i class="sg sg-trash"></i>
                    </button>
                </td>
            </tr>
        `;

        $(".tbodyOSServices").append(newRow);

        // Aplicar máscaras monetárias aos novos campos
        const $newServiceRow = $(".tbodyOSServices tr").last();
        $newServiceRow.find(".valorMask").mask("000.000.000.000.000,00", {
            reverse: true,
            placeholder: "0,00",
        });

        // Inicializar o Select2 com AJAX
        this.initializeServiceSelect(`#selectService${hash}`);

        // Recalcular totais
        this.calculateTotals();
        this.saveItemsToStorage();
        return hash;
    }

    /**
     * Gerar opções de serviços para o select
     */
    generateServiceOptions() {
        if (
            typeof window.services !== "undefined" &&
            window.services.length > 0
        ) {
            return window.services
                .map(
                    (service) =>
                        `<option value="${service.id}">${
                            service.codigo ? service.codigo + " - " : ""
                        }${service.nome}</option>`
                )
                .join("");
        }
        return "";
    }

    /**
     * Inicializar select simples de serviços (sem AJAX)
     */
    initializeSimpleServiceSelect(selector) {
        const $select = $(selector);

        $select.on("change", function () {
            const serviceId = $(this).val();
            const hash = $(this).data("hash");

            if (serviceId && typeof window.services !== "undefined") {
                const service = window.services.find((s) => s.id == serviceId);
                if (service) {
                    // Preencher campos relacionados
                    $(`#id${hash}`).val(service.id);
                    $(`#codigo${hash}`).val(service.codigo || "");
                    $(`#cod${hash}`).val(service.codigo || "");
                    $(`#price${hash}`).val(
                        service.preco ? formatarValor(service.preco) : ""
                    );
                    $(`#serviceName${hash}`).val(service.nome);

                    // Recalcular totais
                    if (typeof window.serviceOrderFormManager !== "undefined") {
                        window.serviceOrderFormManager.calculateTotals();
                    }
                }
            } else {
                // Limpar campos se nenhum serviço selecionado
                $(`#id${hash}`).val("");
                $(`#codigo${hash}`).val("");
                $(`#cod${hash}`).val("");
                $(`#price${hash}`).val("");
                $(`#serviceName${hash}`).val("");
            }
        });
    }

    /**
     * Gerar hash único para novos elementos
     */
    generateHash() {
        return Math.random().toString(36).substr(2, 9);
    }

    /**
     * Inicializar Select2 para produtos
     */
    // Método removido - produtos usam autocomplete, não Select2
    // initializeProductSelect() foi removido pois não é necessário

    /**
     * Inicializar Select2 para serviços - VERSÃO SIMPLIFICADA
     */
    initializeServiceSelect(selector) {
        const $select = $(selector);

        // Verificar se já tem Select2 inicializado
        if ($select.hasClass("select2-hidden-accessible")) {
            return;
        }

        // Preservar opção pré-existente antes de inicializar o Select2
        const existingOption = $select.find("option:selected");
        let preservedValue = null;
        let preservedText = null;

        if (existingOption.length && existingOption.val()) {
            preservedValue = existingOption.val();
            preservedText = existingOption.text();
        }

        $select
            .select2({
                placeholder: "Selecione um serviço ou digite um nome",
                allowClear: true,
                tags: true,
                createTag: function (params) {
                    var term = $.trim(params.term);

                    if (term === "") {
                        return null;
                    }

                    return {
                        id: term,
                        text: term,
                        newTag: true, // Adicionar flag para identificar tags personalizadas
                    };
                },
                ajax: {
                    url: "/OS/search-services",
                    dataType: "json",
                    delay: 250,
                    type: "POST",
                    headers: {
                        "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr(
                            "content"
                        ),
                    },
                    data: function (params) {
                        return {
                            q: params.term || "",
                            page: params.page || 1,
                            _token: $('meta[name="csrf-token"]').attr(
                                "content"
                            ),
                        };
                    },
                    processResults: function (data, params) {
                        params.page = params.page || 1;

                        // Verificar se a resposta tem o formato esperado
                        if (!data || !data.items) {
                            return {
                                results: [],
                                pagination: { more: false },
                            };
                        }

                        // Adicionar sempre o botão "Cadastrar novo serviço" como primeiro item da lista
                        const results = [];

                        // Adicionar item especial para o botão (apenas na primeira página)
                        if (params.page === 1) {
                            results.push({
                                id: "add-new-service",
                                text: "+ Cadastrar novo serviço",
                                disabled: false,
                                isAddButton: true,
                            });
                        }

                        // Verificar se há uma opção preservada que precisa ser mantida
                        const currentValue = preservedValue || $select.val();
                        const currentText =
                            preservedText ||
                            $select.find("option:selected").text();

                        // Se há um valor selecionado e ele não está nos resultados do AJAX, adicionar
                        if (currentValue && currentText && params.page === 1) {
                            const existsInResults = data.items.some(
                                (item) => item.id == currentValue
                            );
                            if (
                                !existsInResults &&
                                currentValue !== "add-new-service"
                            ) {
                                // Adicionar a opção atual no início dos resultados (após o botão)
                                results.push({
                                    id: currentValue,
                                    text: currentText,
                                    selected: true,
                                });
                            }
                        }

                        // Adicionar os itens de serviços após o botão e opção selecionada
                        results.push(...data.items);

                        return {
                            results: results,
                            pagination: {
                                more: params.page * 10 < data.total_count,
                            },
                        };
                    },
                    cache: true,
                },
            })
            .on("select2:error", function (e) {})
            .on("select2:clear", function (e) {
                // Permitir limpeza manual
                $select.data("allow-clear", true);

                // Limpar valor preservado para permitir remoção definitiva
                $select.data("preserved-value", null);

                // Limpar campos relacionados
                const hash = $select.data("hash");
                if (hash) {
                    // Limpar campos visuais
                    $(`#cod${hash}`).val("");
                    $(`#price${hash}`).val("");
                    $(`#originalServiceValue${hash}`).val("");

                    // Resetar mapeamento exclusivo id/nome e sincronizar hidden [codigo]
                    $(`#id${hash}`).val("");
                    $(`#nome${hash}`).val("");
                    $(`#codigo${hash}`).val("");
                    $(`#cod${hash}`).prop("disabled", true);

                    // Recalcular totais
                    if (
                        window.serviceOrderFormManager &&
                        typeof window.serviceOrderFormManager
                            .calculateTotals === "function"
                    ) {
                        window.serviceOrderFormManager.calculateTotals();
                    }
                }

                // Remover permissão de limpeza após um tempo
                setTimeout(() => {
                    $select.data("allow-clear", false);
                }, 100);
            })
            .on("select2:selecting", function (e) {
                // Interceptar seleção do botão de adicionar serviço
                if (e.params.args.data.isAddButton) {
                    e.preventDefault();

                    // Capturar o hash do select2 atual
                    const $currentSelect = $(this);
                    const hash = $currentSelect.data("hash");

                    // Armazenar informações do select atual
                    window.currentServiceSelect = {
                        element: $currentSelect,
                        hash: hash,
                    };

                    // Abrir modal de cadastro de serviço
                    const modal = bootstrap.Modal.getOrCreateInstance(
                        document.getElementById("serviceModal"),
                        { backdrop: true, keyboard: true, focus: true }
                    );
                    modal.show();

                    // Fechar o dropdown do Select2
                    $currentSelect.select2("close");
                }
            });

        // Adicionar evento para capturar cliques no botão "Adicionar Serviço"
        $(document).on("click", ".open-service-modal", function (e) {
            e.preventDefault();
            e.stopPropagation();

            // Capturar o hash do select2 atual
            const $currentSelect = $(this)
                .closest(".select2-container")
                .prev("select");
            if ($currentSelect.length) {
                const hash = $currentSelect.data("hash");
                if (hash) {
                    window.currentServiceHash = hash;
                }
            }

            // Abrir o modal de serviço
            if (typeof bootstrap !== "undefined") {
                const modal = bootstrap.Modal.getOrCreateInstance(
                    document.getElementById("serviceModal"),
                    { backdrop: true, keyboard: true, focus: true }
                );
                modal.show();
            } else if (typeof $ !== "undefined") {
                $("#serviceModal").modal("show");
            }
        });

        // Após inicializar o Select2, restaurar o valor preservado se existir
        if (preservedValue && preservedText) {
            // Aguardar um pouco mais para garantir que o Select2 esteja completamente inicializado
            setTimeout(() => {
                // Criar a opção se ela não existir
                if (
                    $select.find(`option[value="${preservedValue}"]`).length ===
                    0
                ) {
                    const newOption = new Option(
                        preservedText,
                        preservedValue,
                        true,
                        true
                    );
                    $select.append(newOption);
                }

                // Definir o valor e disparar o evento change
                $select.val(preservedValue).trigger("change");

                // Forçar múltiplas atualizações para garantir que o Select2 seja atualizado
                setTimeout(() => {
                    $select.trigger("change.select2");

                    // Verificação adicional para garantir que o valor está visível
                    setTimeout(() => {
                        if ($select.hasClass("select2-hidden-accessible")) {
                            const $select2Container =
                                $select.next(".select2-container");
                            const $selection = $select2Container.find(
                                ".select2-selection__rendered"
                            );

                            // Se o texto ainda não está correto, forçar atualização
                            if (
                                $selection.text() !== preservedText &&
                                $selection.text().trim() === ""
                            ) {
                                $select.select2("destroy");
                                $select.val(preservedValue);

                                // Reinicializar o Select2
                                $select.select2({
                                    placeholder:
                                        "Selecione um serviço ou digite um nome",
                                    allowClear: true,
                                    tags: true,
                                    createTag: function (params) {
                                        var term = $.trim(params.term);

                                        if (term === "") {
                                            return null;
                                        }

                                        return {
                                            id: term,
                                            text: term,
                                            newTag: true,
                                        };
                                    },
                                    ajax: {
                                        url: "/OS/search-services",
                                        dataType: "json",
                                        delay: 250,
                                        data: function (params) {
                                            return {
                                                q: params.term,
                                                page: params.page || 1,
                                            };
                                        },
                                        processResults: function (
                                            data,
                                            params
                                        ) {
                                            params.page = params.page || 1;

                                            const results = (
                                                data.items || []
                                            ).map(function (service) {
                                                return {
                                                    id: service.id,
                                                    text: service.codigo
                                                        ? `#${service.codigo} - ${service.nome}`
                                                        : service.nome,
                                                    data: service,
                                                };
                                            });

                                            // Adicionar botão "Cadastrar novo serviço" se houver termo de busca
                                            if (
                                                params.term &&
                                                params.term.trim() !== ""
                                            ) {
                                                results.unshift({
                                                    id: "add_new_service",
                                                    text: "+ Cadastrar novo serviço",
                                                    disabled: false,
                                                });
                                            }

                                            return {
                                                results: results,
                                                pagination: {
                                                    more:
                                                        params.page * 10 <
                                                        data.total,
                                                },
                                            };
                                        },
                                        cache: true,
                                    },
                                    minimumInputLength: 0,
                                    templateResult: function (data) {
                                        if (data.loading) return data.text;

                                        if (data.id === "add_new_service") {
                                            return $(
                                                '<span style="color: #007bff; font-weight: bold;">' +
                                                    data.text +
                                                    "</span>"
                                            );
                                        }

                                        return data.text;
                                    },
                                    templateSelection: function (data) {
                                        return data.text;
                                    },
                                });

                                $select.trigger("change");
                            }
                        }
                    }, 200);
                }, 100);
            }, 300);
        }
    }

    /**
     * Inicializar selects existentes - VERSÃO SEM AJAX
     */
    initializeExistingSelects() {
        const self = this;

        setTimeout(() => {
            $('select[id^="selectService"]').each(function () {
                const $select = $(this);
                const selectId = $select.attr("id");

                // Verifica se já foi inicializado
                if ($select.data("initialized")) {
                    return;
                }

                // Marcar como inicializado
                $select.data("initialized", true);

                // Inicializar o select com Select2 e AJAX
                self.initializeServiceSelect(`#${selectId}`);

                // Após inicializar, ajustar mapeamento id/nome conforme seleção atual
                const hash = $select.data("hash");
                const $selected = $select.find("option:selected");
                if (hash && $selected.length) {
                    const val = $selected.val();
                    const text = $selected.text();
                    if (val && /^\d+$/.test(val)) {
                        // Serviço existente
                        $(`#id${hash}`).val(val);
                        $(`#nome${hash}`).val("");
                        $(`#cod${hash}`).prop("disabled", true);
                    } else if (text) {
                        // Serviço manual
                        $(`#id${hash}`).val("");
                        $(`#nome${hash}`).val(text);
                        $(`#cod${hash}`).prop("disabled", true);
                    }
                }
            });
        }, 100);
    }

    getStorageKey() {
        const base = this.isEdit ? "so_items_edit" : "so_items_create";
        const path =
            window.location && window.location.pathname
                ? window.location.pathname
                : "unknown";
        return `${base}_${path}`;
    }

    saveItemsToStorage() {
        try {
            const products = [];
            $(".tbodyProducts tr").each(function () {
                const $row = $(this);
                products.push({
                    id: $row.find('input[id^="id"]').val() || "",
                    codigo:
                        $row.find('input[id^="codigo"]').val() ||
                        $row.find('input[id^="cod"]').val() ||
                        "",
                    nome: $row.find('input[name*="[nome]"]').val() || "",
                    qtd: $row.find(".qtdItem").val() || "",
                    custo: $row.find("input.costPrice").val() || "",
                    preco: $row.find('input[id^="price"]').val() || "",
                    descontoPorValor:
                        $row.find(".discountValueItem").val() || "",
                    desconto: $row.find(".discountItem").val() || "",
                    valorTotal: $row.find(".totalValueProduct").val() || "",
                });
            });

            const services = [];
            $(".tbodyOSServices tr").each(function () {
                const $row = $(this);
                const selectedText =
                    $row.find("select.selectService option:selected").text() ||
                    "";
                services.push({
                    id: $row.find('input[id^="id"]').val() || "",
                    nome: $row.find('input[id^="nome"]').val() || "",
                    codigo:
                        $row.find('input[id^="codigo"]').val() ||
                        $row.find('input[id^="cod"]').val() ||
                        "",
                    qtd: $row.find(".qtdItem").val() || "",
                    preco: $row.find('input[id^="price"]').val() || "",
                    descontoPorValor:
                        $row.find(".discountValueItem").val() || "",
                    desconto: $row.find(".discountItem").val() || "",
                    valorTotal: $row.find(".totalValueService").val() || "",
                    selectedText: selectedText,
                });
            });

            const payload = { products, services };
            localStorage.setItem(this.getStorageKey(), JSON.stringify(payload));
        } catch (e) {}
    }

    restoreItemsFromStorage() {
        try {
            const key = this.getStorageKey();
            const raw = localStorage.getItem(key);
            if (!raw) return;

            const data = JSON.parse(raw);
            const hasExisting =
                $(".tbodyProducts tr").length +
                    $(".tbodyOSServices tr").length >
                0;
            if (hasExisting) return;

            if (data && Array.isArray(data.products)) {
                data.products.forEach((p) => {
                    const hash = this.addProductRow();
                    if (typeof hash === "string") {
                        $(`#id${hash}`).val(p.id || "");
                        $(`#codigo${hash}`).val(p.codigo || "");
                        $(`#cod${hash}`).val(p.codigo || "");
                        $(`#productName${hash}`).val(p.nome || "");
                        $(`#qtd${hash}`).val(p.qtd || "");
                        $(`input[data-hash="${hash}"].costPrice`).val(
                            p.custo || ""
                        );
                        $(`#price${hash}`).val(p.preco || "");
                        $(`#discountByValue${hash}`).val(
                            p.descontoPorValor || ""
                        );
                        $(`#discount${hash}`).val(p.desconto || "");
                        $(`#valorTotal${hash}`).val(p.valorTotal || "");
                    }
                });
            }

            if (data && Array.isArray(data.services)) {
                data.services.forEach((s) => {
                    const hash = this.addServiceRow();
                    if (typeof hash === "string") {
                        $(`#id${hash}`).val(s.id || "");
                        $(`#nome${hash}`).val(s.nome || "");
                        $(`#codigo${hash}`).val(s.codigo || "");
                        $(`#cod${hash}`).val(s.codigo || "");
                        $(`#qtd${hash}`).val(s.qtd || "");
                        $(`#price${hash}`).val(s.preco || "");
                        $(`#discountByValue${hash}`).val(
                            s.descontoPorValor || ""
                        );
                        $(`#discount${hash}`).val(s.desconto || "");
                        $(`#valorTotal${hash}`).val(s.valorTotal || "");

                        const $select = $(`#selectService${hash}`);
                        const id = s.id || null;
                        let text = s.selectedText || s.nome || "";
                        if (id && (!text || text.trim() === "")) {
                            if (Array.isArray(window.services)) {
                                const found = window.services.find(
                                    (it) => String(it.id) === String(id)
                                );
                                if (found) {
                                    text = found.codigo
                                        ? `${found.codigo} - ${found.nome}`
                                        : found.nome;
                                }
                            }
                        }
                        if ($select.length) {
                            if (id) {
                                // Se a opção já existe, apenas selecionar; não criar duplicata
                                const exists =
                                    $select.find(`option[value="${id}"]`)
                                        .length > 0;
                                if (exists) {
                                    if (text) {
                                        $select
                                            .find(`option[value="${id}"]`)
                                            .text(text);
                                    }
                                    $select.val(id).trigger("change");
                                } else {
                                    const opt = new Option(text || id, id);
                                    opt.selected = true;
                                    $select.append(opt).trigger("change");
                                }
                            } else if (text) {
                                // Serviço manual (sem id): usar tags, evitando duplicatas por texto
                                const existsText =
                                    $select.find(`option[value="${text}"]`)
                                        .length > 0;
                                if (existsText) {
                                    $select.val(text).trigger("change");
                                } else {
                                    const opt = new Option(text, text);
                                    opt.selected = true;
                                    $select.append(opt).trigger("change");
                                }
                            }
                        }
                    }
                });
            }

            this.calculateTotals();
        } catch (e) {}
    }

    clearItemsStorage() {
        try {
            localStorage.removeItem(this.getStorageKey());
        } catch (e) {}
    }

    /**
     * Configurar seletor de status (apenas para edit)
     */
    setupStatusSelect() {
        if (!this.isEdit) return;

        const $statusSelect = $("#selectStatusOS");
        if (!$statusSelect.length) return;

        // Como as opções agora são renderizadas diretamente no Blade,
        // apenas precisamos preservar o valor inicial e configurar validação
        const currentStatus =
            $statusSelect.val() || $statusSelect.data("current-status");

        // Preservar o valor inicial
        if (currentStatus) {
            $statusSelect.data("original-value", currentStatus);
        }

        // Adicionar validação de fluxo de trabalho
        this.setupStatusWorkflowValidation($statusSelect, currentStatus);
    }

    /**
     * Obter opções de status disponíveis baseado no status atual
     */
    getAvailableStatusOptions(allOptions, currentStatus, allowedTransitions) {
        if (!currentStatus) {
            return allOptions; // Se não há status atual, mostrar todas as opções
        }

        // Sempre incluir o status atual
        const availableOptions = allOptions.filter(
            (option) => option.value === currentStatus
        );

        // Adicionar transições permitidas
        const allowedNextStatuses = allowedTransitions[currentStatus] || [];

        allowedNextStatuses.forEach((nextStatus) => {
            const nextOption = allOptions.find(
                (option) => option.value === nextStatus
            );
            if (
                nextOption &&
                !availableOptions.find((opt) => opt.value === nextStatus)
            ) {
                availableOptions.push(nextOption);
            }
        });

        return availableOptions;
    }

    /**
     * Configurar validação de fluxo de trabalho do status
     */
    setupStatusWorkflowValidation($statusSelect, originalStatus) {
        // Definir transições permitidas (mesmo que no backend)
        const allowedTransitions = {
            rascunho: ["em aberto"],
            "em aberto": ["concluido", "cancelado"],
            concluido: ["em aberto"],
            cancelado: ["em aberto"],
        };

        $statusSelect.on("change", (e) => {
            const newStatus = $(e.target).val();
            const currentStatus =
                $statusSelect.data("original-value") || originalStatus;

            // Se o status não mudou, não precisa validar
            if (currentStatus === newStatus) {
                return;
            }

            // Verificar se a transição é permitida
            const allowedForCurrent = allowedTransitions[currentStatus] || [];

            if (!allowedForCurrent.includes(newStatus)) {
                // Mostrar alerta e reverter para o status original
                this.showStatusTransitionError(currentStatus, newStatus);
                $statusSelect.val(currentStatus);
                return false;
            }

            // Atualizar o status original para futuras validações
            $statusSelect.data("original-value", newStatus);
        });
    }

    /**
     * Mostrar erro de transição de status
     */
    showStatusTransitionError(currentStatus, newStatus) {
        const statusNames = {
            rascunho: "Rascunho",
            "em aberto": "Em Aberto",
            concluido: "Concluído",
            cancelado: "Cancelado",
        };

        const currentName = statusNames[currentStatus] || currentStatus;
        const newName = statusNames[newStatus] || newStatus;

        // Usar SweetAlert se disponível, senão usar alert padrão
        if (typeof Swal !== "undefined") {
            Swal.fire({
                icon: "error",
                title: "Transição de Status Inválida",
                text: `Não é possível alterar o status de '${currentName}' para '${newName}'`,
                confirmButtonText: "Entendi",
            });
        } else {
            alert(
                `Não é possível alterar o status de '${currentName}' para '${newName}'`
            );
        }
    }

    /**
     * Detectar o status atual da página
     */
    getCurrentStatusFromPage() {
        // Método auxiliar mantido para compatibilidade, mas não mais necessário
        // pois as opções são renderizadas no Blade
        const $statusSelect = $("#selectStatusOS");
        return (
            $statusSelect.data("current-status") ||
            $statusSelect.val() ||
            "rascunho"
        );
    }

    /**
     * Configurar manipuladores de cliente/veículo (apenas para edit)
     */
    setupClientVehicleHandlers() {
        if (!this.isEdit) return;
        // Implementar lógica específica de cliente/veículo para edit
    }

    /**
     * Configurar manipuladores de data (apenas para edit)
     */
    setupDateHandlers() {
        if (!this.isEdit) return;
        // Implementar lógica específica de datas para edit
    }

    /**
     * Configurar manipuladores de notificação (apenas para edit)
     */
    setupNotificationHandlers() {
        if (!this.isEdit) return;
        // Implementar lógica específica de notificações para edit
    }

    /**
     * Configurar manipuladores de KM do veículo
     */
    setupVehicleKmHandlers() {
        $(document).on("change", "#selectVehicle", (e) => {
            const selectedVehicleId = $(e.target).val();
            const $inputKmAtual = $("#inputKmAtual");
            const $inputKmUltimo = $("#inputKmUltimo");

            // Desabilitar campo KM atual inicialmente
            $inputKmAtual.prop("disabled", true);

            if (!selectedVehicleId) {
                $inputKmAtual.prop("disabled", true);
                return;
            }

            // Habilitar campo KM atual
            $inputKmAtual.prop("disabled", false);

            // Construir URL baseada no modo (create/edit)
            let url = `/veiculos/${selectedVehicleId}/buscar-km-veiculo?edit=false`;
            if (this.isEdit && this.config.orderId) {
                url = `/veiculos/${selectedVehicleId}/buscar-km-veiculo?edit=true&orderId=${this.config.orderId}`;
            }

            // Buscar último KM do veículo
            $.ajax({
                url: url,
                method: "GET",
                success: (response) => {
                    $inputKmAtual.prop("disabled", false);

                    if (response.km_ultimo) {
                        $inputKmUltimo.val(response.km_ultimo);
                    } else {
                        $inputKmUltimo.val(0);
                    }

                    // Aplicar máscara após preencher o valor
                    $inputKmUltimo.trigger("input");
                },
                error: (xhr, status, error) => {
                    $inputKmUltimo.val(0);
                    // Aplicar máscara após preencher o valor
                    $inputKmUltimo.trigger("input");
                },
            });
        });
    }

    /**
     * Configurar autocomplete de produtos e serviços para edição
     */
    setupEditAutocomplete() {
        if (!this.isEdit) return;

        // Map para armazenar instâncias de autocomplete
        const autocompleteInstances = new Map();

        // Função para inicializar autocomplete de produtos
        const initProductAutocomplete = (input, hash) => {
            const inputSelector = `#product${hash}`;

            // Verificar se já existe uma instância
            if (autocompleteInstances.has(inputSelector)) {
                return autocompleteInstances.get(inputSelector);
            }

            const instance = new Autocomplete(input, {
                data: window.products || [],
                maximumItems: 10,
                treshold: 1,
                onSelectItem: ({ label, value }) => {
                    const product = window.products.find((p) => p.id == value);
                    if (!product) return;

                    // Preencher campos do produto
                    $(`#cod${hash}`).val(product.codigo || "");

                    // Verificar se o valor não é null ou zero antes de preencher campo de preço
                    const valorProduto = product.valor;
                    const valorNumerico = valorProduto
                        ? parseFloat(valorProduto.toString().replace(",", "."))
                        : null;

                    if (valorProduto && valorNumerico > 0) {
                        $(`#price${hash}`).val(product.valor);
                        $(`#totalValue${hash}`).val(product.valor);
                        $(`#valorTotal${hash}`).val(valorNumerico.toFixed(2));
                    }

                    // Verificar se o custo não é null ou zero antes de preencher campo de custo
                    const custoProduto = product.custo;
                    const custoNumerico = custoProduto
                        ? parseFloat(custoProduto.toString().replace(",", "."))
                        : null;

                    if (custoProduto && custoNumerico > 0) {
                        $(`input[data-hash="${hash}"].costPrice`).val(
                            product.custo
                        );
                    }

                    $(`#qtd${hash}`).val(1);
                    calculaSubTotalItems();
                    // Fechar o autocomplete após seleção
                    this.close();
                },
            });

            autocompleteInstances.set(inputSelector, instance);
            return instance;
        };

        // Função para inicializar autocomplete de serviços
        const initServiceAutocomplete = (input, hash) => {
            const inputSelector = `#service${hash}`;

            // Verificar se já existe uma instância
            if (autocompleteInstances.has(inputSelector)) {
                return autocompleteInstances.get(inputSelector);
            }

            const instance = new Autocomplete(input, {
                data: window.services || [],
                maximumItems: 10,
                treshold: 1,
                onSelectItem: ({ label, value }) => {
                    const service = window.services.find((s) => s.id == value);
                    if (!service) return;

                    // Preencher campos do serviço
                    $(`#cod${hash}`).val(service.codigo || "");

                    // Verificar se o valor não é null ou zero antes de preencher campo de preço
                    const valorServico = service.valor;
                    const valorNumerico = valorServico
                        ? parseFloat(valorServico.toString().replace(",", "."))
                        : null;

                    if (valorServico && valorNumerico > 0) {
                        $(`#price${hash}`).val(service.valor);
                        $(`#totalValue${hash}`).val(service.valor);
                        $(`#valorTotal${hash}`).val(valorNumerico.toFixed(2));
                    }

                    $(`#qtd${hash}`).val(1);
                    calculaSubTotalItems();
                    // Fechar o autocomplete após seleção
                    this.close();
                },
            });

            autocompleteInstances.set(inputSelector, instance);
            return instance;
        };

        // Inicializar autocompletes quando os campos são focados
        $(document).on("focus", ".autocompleteInputProduct", function () {
            const hash = $(this).data("hash");
            initProductAutocomplete(this, hash);
        });

        $(document).on("focus", ".autocompleteInputService", function () {
            const hash = $(this).data("hash");
            initServiceAutocomplete(this, hash);
        });
    }

    /**
     * Configurar funcionalidade de copiar link
     */
    setupCopyLinkHandler() {
        if (!this.isEdit) return;

        const copyBtn = document.getElementById("btnCopyLink");
        if (!copyBtn) return;

        // Inicializar tooltip bootstrap no botão (se Bootstrap estiver presente)
        let copyTooltip = null;
        if (window.bootstrap && window.bootstrap.Tooltip) {
            copyTooltip = new bootstrap.Tooltip(copyBtn);
        }

        copyBtn.addEventListener("click", async function () {
            const url = this.getAttribute("data-url");
            if (!url) return;

            try {
                await navigator.clipboard.writeText(url);
                // Atualizar tooltip temporariamente
                if (copyTooltip) {
                    copyBtn.setAttribute(
                        "data-bs-original-title",
                        "Link copiado com sucesso!"
                    );
                    copyTooltip.show();
                    setTimeout(function () {
                        copyTooltip.hide();
                        copyBtn.setAttribute(
                            "data-bs-original-title",
                            "Copiar link"
                        );
                    }, 1500);
                }
            } catch (error) {
                console.error("Erro ao copiar link:", error);
            }
        });
    }

    /**
     * Configurar funcionalidade do WhatsApp
     */
    setupWhatsAppHandler() {
        if (!this.isEdit) return;

        // Função para normalizar telefone
        const normalizePhone = (p) => {
            if (!p) return "";
            const digits = String(p).replace(/\D/g, "");
            // Se não vier DDI, assume Brasil (55)
            if (digits.length <= 11) return "55" + digits;
            return digits;
        };

        const waBtn = document.getElementById("btnWhatsapp");
        if (!waBtn) return;

        waBtn.addEventListener("click", function () {
            const raw = this.getAttribute("data-phone") || "";
            const phone = normalizePhone(raw);
            if (!phone) return;
            window.open("https://wa.me/".concat(phone), "_blank");
        });
    }

    /**
     * Carregar notificações (apenas para edit)
     */
    loadNotifications() {
        if (!this.isEdit) return;
        this.notificationsLoaded = true;
        // Implementar lógica de carregamento de notificações
    }

    /**
     * Configurar validação de formulário e modal de confirmação
     */
    setupFormValidationAndModal() {
        if (!this.isEdit) return;

        const form = document.querySelector(".formCreateServcieOrder");
        const $form = $(".formCreateServcieOrder");
        const modalEl = document.getElementById("confirmWhatsAppModal");

        // Não dependa da existência do modal para habilitar o botão Salvar
        if (!form) return;

        const modal = modalEl ? new bootstrap.Modal(modalEl) : null;

        // Status original no carregamento
        const statusOriginal = $("#selectStatusOS").val();
        let statusModify = "";

        // Atualiza statusModify ao trocar o status
        $("#selectStatusOS").on("change", function () {
            statusModify = $(this).val();
        });

        const camposMonitorados = [
            "inputDueDate",
            "selectColaborator",
            "selectStatusOS",
            "autocompleteInputClient",
            "autocompleteInputVehicle",
            "inputObservations",
            "valueFinalOS",
        ];

        const valoresOriginais = {};

        camposMonitorados.forEach((id) => {
            const el = $("#" + id);
            if (el.length) {
                valoresOriginais[id] = el.val();
            }
        });

        function houveMudanca() {
            return camposMonitorados.some((id) => {
                const el = $("#" + id);
                return el.length && el.val() !== valoresOriginais[id];
            });
        }

        $("#openModalBtn").on("click", function (e) {
            e.preventDefault();
            $form.addClass("was-validated");

            if (!form.checkValidity()) return;

            const mudou = houveMudanca();
            const statusAtual = statusModify || statusOriginal;

            // Se o status for "rascunho", envia direto, ignorando mudanças
            if (statusAtual === "rascunho") {
                $form.trigger("submit");
                return;
            }

            if (mudou) {
                // Verifica se a oficina tem notificações habilitadas e telefone configurado
                const notificacoesHabilitadas =
                    window.garageNotificationsEnabled || false;
                const telefoneNotificacao =
                    window.garageNotificationPhone || "";

                if (statusModify !== "") {
                    $form.append(
                        '<input type="hidden" name="statusModify" value="true">'
                    );
                }

                // Se notificações estão desabilitadas, telefone não configurado OU não há modal, envia direto
                if (
                    !notificacoesHabilitadas ||
                    !telefoneNotificacao.trim() ||
                    !modal
                ) {
                    $form.append(
                        '<input type="hidden" name="notificarCliente" value="0">'
                    );
                    $form.trigger("submit");
                } else {
                    modal.show();
                }
            } else {
                $form.trigger("submit");
            }
        });

        $("#confirmYes").on("click", function () {
            $form.append(
                '<input type="hidden" name="notificarCliente" value="1">'
            );
            if (modal) modal.hide();
            setTimeout(() => $form.trigger("submit"), 200);
            window.onbeforeunload = null;
        });

        $("#confirmNo").on("click", function () {
            $form.append(
                '<input type="hidden" name="notificarCliente" value="0">'
            );
            if (modal) modal.hide();
            setTimeout(() => $form.trigger("submit"), 200);
            window.onbeforeunload = null;
        });
    }

    /**
     * Configurar validação condicional dos campos obrigatórios
     */
    setupConditionalValidation() {
        const requiredFields = [
            "#selectColaborator",
            "#selectClient",
            "#selectVehicle",
        ];

        // Validar campos quando perdem o foco
        requiredFields.forEach((selector) => {
            $(selector).on("blur", () => {
                const currentStatus = $("#selectStatusOS").val();
                this.validateRequiredFieldsByStatus(currentStatus);
            });
        });

        // Validar antes da submissão do formulário
        $(".formCreateServcieOrder").on("submit", (e) => {
            const currentStatus = $("#selectStatusOS").val();
            if (!this.validateRequiredFieldsByStatus(currentStatus)) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        });

        // Sincronizar atributos 'required' conforme o status atual
        const syncRequired = () => {
            const currentStatus = $("#selectStatusOS").val();
            this.syncRequiredAttributesByStatus(currentStatus);
        };

        $("#selectStatusOS").on("change", syncRequired);
        // Inicializa na carga
        syncRequired();
    }

    /**
     * Validar campos obrigatórios baseado no status da OS
     */
    validateRequiredFieldsByStatus(status) {
        // Se for rascunho, não precisa validar
        if (status === "rascunho") {
            this.clearValidationErrors();
            return true;
        }

        let isValid = true;
        const fieldsToValidate = [
            {
                selector: "#selectColaborator",
                name: "Responsável",
                errorSelector: "#selectColaborator",
            },
            {
                selector: "#selectClient",
                name: "Cliente",
                errorSelector: "#selectClient",
            },
            {
                selector: "#selectVehicle",
                name: "Veículo",
                errorSelector: "#selectVehicle",
            },
        ];

        let firstInvalid = null;
        fieldsToValidate.forEach((field) => {
            const $element = $(field.selector);
            const value = $element.val();

            if (!value || value.trim() === "") {
                this.showFieldError(
                    $element,
                    `${field.name} é obrigatório quando o status não é "Rascunho".`
                );
                isValid = false;
                if (!firstInvalid) firstInvalid = $element;
            } else {
                this.clearFieldError($element);
            }
        });

        if (!isValid && firstInvalid) {
            const $form = $(".formCreateServcieOrder");
            $form.addClass("was-validated");
            try {
                const top = firstInvalid.offset().top - 120;
                window.scrollTo({ top, behavior: "smooth" });
                firstInvalid.focus();
            } catch (e) {}
        }

        return isValid;
    }

    // Adiciona ou remove 'required' dos campos conforme o status (rascunho = não obrigatório)
    syncRequiredAttributesByStatus(currentStatus) {
        const isDraft =
            (currentStatus || "").toString().toLowerCase() === "rascunho";

        const fields = [
            $("#selectClient"),
            $("#selectVehicle"),
            $("#selectColaborator"),
            $("#selectEmployee"),
        ];

        fields.forEach(($f) => {
            if (!$f || !$f.length) return;
            $f.prop("required", !isDraft);
        });
    }

    /**
     * Mostrar erro de validação em um campo
     */
    showFieldError($element, message) {
        $element.addClass("is-invalid");

        // Encontrar ou criar o elemento de feedback
        let $feedback = $element.siblings(".invalid-feedback");
        if ($feedback.length === 0) {
            $feedback = $('<div class="invalid-feedback"></div>');
            $element.after($feedback);
        }

        $feedback.text(message).show();
    }

    /**
     * Limpar erro de validação de um campo
     */
    clearFieldError($element) {
        $element.removeClass("is-invalid");
        $element.siblings(".invalid-feedback").hide();
    }

    /**
     * Limpar todos os erros de validação
     */
    clearValidationErrors() {
        const fieldsToValidate = [
            "#selectColaborator",
            "#selectClient",
            "#selectVehicle",
        ];

        fieldsToValidate.forEach((selector) => {
            const $element = $(selector);
            this.clearFieldError($element);
        });
    }

    /**

     * Configurar modal de confirmação para criação de OS
     */
    setupCreateFormModal() {
        if (!this.isCreate) return;

        const form = document.querySelector(".formCreateServcieOrder");
        const $form = $(".formCreateServcieOrder");
        const modalEl = document.getElementById("confirmWhatsAppModal");
        const submitBtn = document.getElementById("openModalBtn");

        if (!form || !modalEl || !submitBtn) return;

        const modal = bootstrap.Modal.getOrCreateInstance(modalEl, { backdrop: true, keyboard: true, focus: true });

        // Variáveis globais definidas nos templates Blade
        const globalsDefined =
            typeof window.garageNotificationsEnabled !== "undefined" &&
            typeof window.garageNotificationPhone !== "undefined";
        const notificacoesHabilitadas = !!window.garageNotificationsEnabled;
        const telefoneNotificacao = (
            window.garageNotificationPhone || ""
        ).toString();

        $("#openModalBtn").on("click", (e) => {
            e.preventDefault();

            $form.addClass("was-validated");
            if (!form.checkValidity()) return;

            const statusAtual = $("#selectStatusOS").val();

            // Se status for rascunho, salva direto
            if (statusAtual === "rascunho") {
                this.submitConfirmed = true;
                $form.trigger("submit");
                return;
            }

            // Se as variáveis globais estiverem definidas e notificações desabilitadas ou telefone não configurado, envia sem modal
            if (
                globalsDefined &&
                (!notificacoesHabilitadas || !telefoneNotificacao.trim())
            ) {
                $form.append(
                    '<input type="hidden" name="notificarCliente" value="0">'
                );
                this.submitConfirmed = true;
                $form.trigger("submit");
            } else {
                modal.show();
            }
        });

        $("#confirmYes").on("click", () => {
            $form.append(
                '<input type="hidden" name="notificarCliente" value="1">'
            );
            modal.hide();
            this.submitConfirmed = true;
            setTimeout(() => $form.trigger("submit"), 200);
            window.onbeforeunload = null;
        });

        $("#confirmNo").on("click", () => {
            $form.append(
                '<input type="hidden" name="notificarCliente" value="0">'
            );
            modal.hide();
            this.submitConfirmed = true;
            setTimeout(() => $form.trigger("submit"), 200);
            window.onbeforeunload = null;
        });
    }

    /**
     * Configurar validação básica de formulários
     */
    setupBasicFormValidation() {
        // Validação básica para todos os formulários com needs-validation
        (() => {
            "use strict";
            const forms = document.querySelectorAll(".needs-validation");
            Array.from(forms).forEach((form) => {
                form.addEventListener(
                    "submit",
                    (event) => {
                        if (!form.checkValidity()) {
                            event.preventDefault();
                            event.stopPropagation();
                        }
                        form.classList.add("was-validated");
                    },
                    false
                );
            });
        })();
    }

    /**
     * Preservar valores iniciais dos selects antes da inicialização
     */
    preserveInitialValues() {
        if (!this.isEditing) return;

        // Preservar valores dos selects de serviços
        $('select[name*="servicos"][name*="[servico_id]"]').each(
            (index, select) => {
                const $select = $(select);
                const value = $select.val();
                const text = $select.find("option:selected").text();

                if (value && value !== "") {
                    this.preservedValues.set($select.attr("id"), {
                        value: value,
                        text: text,
                    });
                }
            }
        );

        // Preservar valores dos selects de produtos
        $('select[name*="produtos"][name*="[produto_id]"]').each(
            (index, select) => {
                const $select = $(select);
                const value = $select.val();
                const text = $select.find("option:selected").text();

                if (value && value !== "") {
                    this.preservedValues.set($select.attr("id"), {
                        value: value,
                        text: text,
                    });
                }
            }
        );
    }

    /**
     * Restaurar valores preservados após inicialização dos selects
     */
    restorePreservedValues() {
        if (!this.isEditing || this.preservedValues.size === 0) return;

        this.preservedValues.forEach((data, selectId) => {
            const $select = $(`#${selectId}`);

            if ($select.length && data.value) {
                // Verificar se a opção existe, se não, criar
                if (
                    $select.find(`option[value="${data.value}"]`).length === 0
                ) {
                    $select.append(
                        `<option value="${data.value}">${data.text}</option>`
                    );
                }

                // Definir o valor
                $select.val(data.value);

                // Forçar atualização do Select2 se estiver inicializado
                if ($select.hasClass("select2-hidden-accessible")) {
                    $select.trigger("change.select2");
                }
            }
        });

        this.initializationComplete = true;
    }

    initializeClientVehicleSelects() {
        if (typeof $.fn.select2 !== "function") return;
        const self = this;
        const $client = $("#selectClient");
        const $vehicle = $("#selectVehicle");

        // Ensure vehicle select is enabled
        $vehicle.prop("disabled", false);

        const initSelect = ($select, type) => {
            if (!$select.length) return;
            
            // Force re-initialization to ensure our logic (with Add Client button) is applied
            if ($select.hasClass("select2-hidden-accessible")) {
                 $select.select2('destroy');
            }

            const ajaxCfg =
                type === "client"
                    ? {
                          url: "/clientes/getClients",
                          type: "POST",
                          delay: 300,
                          data: (params) => ({ value: params.term || "" }),
                          headers: {
                              "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr(
                                  "content"
                              ),
                          },
                          processResults: function (response) {
                              const results = [];
                              results.push({
                                  id: "__add_client__",
                                  text: "Adicionar cliente",
                                  isAddButton: true,
                              });
                              // Check for 'clients' or 'results'
                              const items = response.clients || response.results;
                              if (items) {
                                  items.forEach((item) => {
                                      const text = item.text || (item.tipo_cliente === "PF"
                                              ? item.nome || ""
                                              : item.razao_social || "");
                                      results.push({ id: item.id, text: text });
                                  });
                              }
                              return { results };
                          },
                      }
                    : {
                          url: "/veiculos/getVehicles",
                          type: "POST",
                          delay: 300,
                          data: (params) => ({
                              value: params.term || "",
                              cliente: $("#selectClient").val() || "",
                          }),
                          headers: {
                              "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr(
                                  "content"
                              ),
                          },
                          processResults: function (response) {
                              const results = [];
                              results.push({
                                  id: "__add_vehicle__",
                                  text: "Cadastrar novo veículo",
                                  isAddButton: true,
                              });
                              if (response.results) {
                                  response.results.forEach((item) => {
                                      const text = `${item.placa || ""} - ${
                                          item.nomeMarca || item.marca || ""
                                      } ${
                                          item.nomeModelo || item.modelo || ""
                                      }`.trim();
                                      results.push({ 
                                          id: item.id, 
                                          text: text,
                                          owner_id: item.owner_id,
                                          owner_name: item.owner_name
                                      });
                                  });
                              }
                              return { results };
                          },
                      };

            $select
                .select2({
                    placeholder:
                        type === "client"
                            ? "Selecione um cliente"
                            : "Selecione um veículo",
                    allowClear: true,
                    width: "100%",
                    ajax: ajaxCfg,
                    escapeMarkup: function (markup) {
                        return markup;
                    },
                    language: {
                        noResults: () => {
                            const label =
                                type === "client"
                                    ? "Adicionar cliente"
                                    : "Cadastrar novo veículo";
                            const cls =
                                type === "client"
                                    ? "select2-add-client"
                                    : "select2-add-vehicle";
                            return `<span class="${cls}" style="cursor:pointer; color: #007bff; font-weight: bold;"><i class="sg sg-plus-circle me-1"></i>${label}</span>`;
                        },
                    },
                    templateResult: function (data) {
                        if (data.isAddButton) {
                            const label =
                                data.id === "__add_client__"
                                    ? "Adicionar cliente"
                                    : "Cadastrar novo veículo";
                            return $(
                                '<span style="color: #007bff; font-weight: bold;"><i class="sg sg-plus-circle me-1"></i>' +
                                    label +
                                    "</span>"
                            );
                        }
                        return data.text;
                    },
                })
                .on("select2:selecting", function (e) {
                    const data = e.params.args.data;
                    if (data.isAddButton) {
                        e.preventDefault();
                        if (type === "client")
                            self.openQuickCreateClientModal();
                        else self.openQuickCreateVehicleModal();
                        $(this).select2("close");
                    }
                })
                .on("select2:open", function () {
                    const $dropdownMsg = $(".select2-results__message");
                    $dropdownMsg.off("click.select2Add");
                    $dropdownMsg.on(
                        "click.select2Add",
                        ".select2-add-client",
                        function (ev) {
                            ev.preventDefault();
                            self.openQuickCreateClientModal();
                            $select.select2("close");
                        }
                    );
                    $dropdownMsg.on(
                        "click.select2Add",
                        ".select2-add-vehicle",
                        function (ev) {
                            ev.preventDefault();
                            self.openQuickCreateVehicleModal();
                            $select.select2("close");
                        }
                    );
                });
        };

        initSelect($client, "client");
        initSelect($vehicle, "vehicle");

        // Logic to sync Client <-> Vehicle
        let isProgrammaticChange = false;

        $vehicle.on('select2:select', function(e) {
             const data = e.params.data;
             if (data.owner_id) {
                 if ($client.val() != data.owner_id) {
                     isProgrammaticChange = true;
                     
                     // Create option if missing
                     if ($client.find("option[value='" + data.owner_id + "']").length === 0) {
                         const newOption = new Option(data.owner_name || 'Cliente', data.owner_id, true, true);
                         $client.append(newOption);
                     }
                     
                     $client.val(data.owner_id).trigger('change');
                     isProgrammaticChange = false;
                 }
             }
        });

        $client.on('change', function() {
            if (!isProgrammaticChange) {
                $vehicle.val(null).trigger('change');

                const clientId = $(this).val();
                if (clientId) {
                    $.ajax({
                        url: "/veiculos/getVehicles",
                        type: "POST",
                        data: {
                            cliente: clientId,
                            value: ""
                        },
                        headers: {
                            "X-CSRF-TOKEN": $('meta[name="csrf-token"]').attr("content"),
                        },
                        success: function(response) {
                            if (response.results && response.results.length === 1) {
                                const item = response.results[0];
                                if ($vehicle.find("option[value='" + item.id + "']").length === 0) {
                                    const newOption = new Option(item.text, item.id, true, true);
                                    $vehicle.append(newOption);
                                }
                                $vehicle.val(item.id).trigger('change');
                            }
                        }
                    });
                }
            }
        });
    }
}

// Função global para selecionar serviço no Select2 após cadastro
window.selectServiceInSelect2 = function (service, hash) {
    if (hash) {
        const $select = $(`select[data-hash="${hash}"]`);

        if ($select.length) {
            // Permitir substituição temporariamente
            $select.data("allow-clear", true);

            // Adicionar nova opção se não existir
            if ($select.find(`option[value="${service.id}"]`).length === 0) {
                $select.append(
                    `<option value="${service.id}">${service.nome_servico}</option>`
                );
            }

            // Selecionar o serviço
            $select.val(service.id).trigger("change");

            // Atualizar valor preservado com o novo serviço
            $select.data("preserved-value", service.id);

            // Mapear como existente: setar id e limpar nome, bloquear código
            $(`#id${hash}`).val(service.id);
            $(`#nome${hash}`).val("");
            $(`#cod${hash}`).prop("disabled", true);
            $(`#codigo${hash}`).val(service.codigo || "");

            // Preencher campos relacionados usando o hash

            // Preencher código usando o ID correto
            const $codigoField = $(`#cod${hash}`);

            $codigoField.val(service.codigo || "");

            // Preencher valor de venda usando o ID correto
            if (service.valor_servico) {
                const valorNumerico = service.valor_servico
                    ? parseFloat(
                          service.valor_servico.toString().replace(",", ".")
                      )
                    : null;

                if (valorNumerico > 0) {
                    const $priceField = $(`#price${hash}`);

                    $priceField.val(service.valor_servico);

                    // Aplicar máscara ao campo de preço
                    $priceField.mask("#.##0,00", { reverse: true });

                    // Disparar evento change para recalcular totais
                    $priceField.trigger("change");
                }
            }

            // Recalcular totais se a função existir
            if (
                window.serviceOrderFormManager &&
                typeof window.serviceOrderFormManager.calculateTotals ===
                    "function"
            ) {
                window.serviceOrderFormManager.calculateTotals();
            }

            // Remover permissão de limpeza após um breve delay
            setTimeout(() => {
                $select.data("allow-clear", false);
            }, 100);

            // Limpar hash atual
            window.currentServiceHash = null;
        } else {
        }
    } else {
    }
};

// Exportar para uso global
window.ServiceOrderFormManager = ServiceOrderFormManager;

// Inicialização automática baseada na página atual
$(document).ready(function () {
    // Verificar se já existe uma instância para evitar duplicação
    if (window.serviceOrderFormManager) {
        return;
    }

    // Usar o modo passado no construtor em vez de detectar pela URL
    if (
        window.serviceOrderFormManager &&
        window.serviceOrderFormManager.mode === "edit"
    ) {
        // Já inicializado para edição
    } else if (
        window.serviceOrderFormManager &&
        window.serviceOrderFormManager.mode === "create"
    ) {
        // Já inicializado para criação
    } else {
        // Fallback: inicializar baseado na presença de form de edição
        const editForm = document.querySelector(
            'form[action*="orders"][action*="update"], form[method="post"] input[name="_method"][value="PUT"]'
        );
        if (editForm) {
            window.serviceOrderFormManager = new ServiceOrderFormManager(
                "edit",
                window.serviceOrderConfig || {}
            );
        } else {
            window.serviceOrderFormManager = new ServiceOrderFormManager(
                "create",
                window.serviceOrderConfig || {}
            );
        }
    }
});
